import { LatencyTier, LATENCY_TIERS, TIER_LABEL } from '../chunk-5EGRE2B4.js';
export { Completeness, LATENCY_TIERS, LatencyTier, TIER_LABEL, isAtLeast } from '../chunk-5EGRE2B4.js';
import { customerText } from '../chunk-BKXDATAH.js';
import { randomHex } from '../chunk-WVLHT3DL.js';
import { RUNG_ORDER, canonicalRung } from '../chunk-INX63VAT.js';
import { asString, normalizeAddress, isValidAddress, num } from '../chunk-7D3TZUJS.js';
import { toCanonicalChainId, resolveChain, chainDisplayName, DEFAULT_CHAIN_ID } from '../chunk-R5ZQC5YM.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-PZ5AY32C.js';

// src/pipeline/policies.ts
var FailurePolicy = {
  /** Abort the whole pipeline run. */
  FAIL_PIPELINE: "fail_pipeline",
  /** Mark this step failed; skip steps that `needs` it; continue others. */
  FAIL_STEP: "fail_step",
  /** Soft failure; emit a degraded result and continue. */
  DEGRADE: "degrade"
};
var FAILURE_POLICIES = [
  FailurePolicy.FAIL_PIPELINE,
  FailurePolicy.FAIL_STEP,
  FailurePolicy.DEGRADE
];
var FAILURE_POLICY_LABEL = {
  [FailurePolicy.FAIL_PIPELINE]: "Fail pipeline",
  [FailurePolicy.FAIL_STEP]: "Fail step",
  [FailurePolicy.DEGRADE]: "Degrade"
};
var FAILURE_POLICY_DESCRIPTION = {
  [FailurePolicy.FAIL_PIPELINE]: "Abort the whole run if this step raises.",
  [FailurePolicy.FAIL_STEP]: "Fail this step and skip its dependents; continue the rest.",
  [FailurePolicy.DEGRADE]: "Treat as a soft failure; emit a degraded result and continue."
};
var StepStatus = {
  OK: "ok",
  DEGRADED: "degraded",
  FAILED: "failed",
  // First-class skip (feature flag off, tier unsupported, upstream skipped,
  // dependency not wired, in-step no-op). Mirrors
  // `authe_tx_pipeline.core.step.StepStatus.SKIPPED`; never rolls a run up to
  // `failed`. The `StepDecision` on the outcome carries *why*.
  SKIPPED: "skipped"
};
var STEP_STATUS_LABEL = {
  [StepStatus.OK]: "OK",
  [StepStatus.DEGRADED]: "Degraded",
  [StepStatus.FAILED]: "Failed",
  [StepStatus.SKIPPED]: "Skipped"
};
var DEFAULT_RETRY_POLICY = {
  maxAttempts: 1,
  initialBackoffSeconds: 0.5,
  backoffMultiplier: 2,
  maxBackoffSeconds: 30
};
function retriesEnabled(policy) {
  return policy.maxAttempts > 1;
}

// src/pipeline/step.ts
var StepCategory = {
  SOURCE: "source",
  NORMALIZE: "normalize",
  DECODE: "decode",
  PROTOCOL: "protocol",
  ENRICH: "enrich",
  GRAPH: "graph",
  GROUPING: "grouping",
  PERSIST: "persist",
  FINALITY: "finality",
  TRIGGERS: "triggers",
  ML: "ml",
  METERING: "metering",
  GENERAL: "general",
  CUSTOM: "custom"
};
var STEP_CATEGORY_LABEL = {
  [StepCategory.SOURCE]: "Sources",
  [StepCategory.NORMALIZE]: "Normalize",
  [StepCategory.DECODE]: "Decode & Parse",
  [StepCategory.PROTOCOL]: "Protocol",
  [StepCategory.ENRICH]: "Enrichment",
  [StepCategory.GRAPH]: "Graph & Entity",
  [StepCategory.GROUPING]: "Grouping / SAGA",
  [StepCategory.PERSIST]: "Persistence",
  [StepCategory.FINALITY]: "Reorg & Finality",
  [StepCategory.TRIGGERS]: "Triggers",
  [StepCategory.ML]: "ML",
  [StepCategory.METERING]: "Quota & Billing",
  [StepCategory.GENERAL]: "General",
  [StepCategory.CUSTOM]: "Custom"
};
var LEGACY_CATEGORY_ALIAS = {
  trigger: StepCategory.TRIGGERS,
  quota: StepCategory.METERING,
  reorg: StepCategory.FINALITY,
  correlate: StepCategory.GROUPING,
  index: StepCategory.PROTOCOL
};
function normalizeStepCategory(value) {
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === "") return StepCategory.GENERAL;
  return LEGACY_CATEGORY_ALIAS[raw] ?? raw;
}
function stepCategoryLabel(category) {
  const known = STEP_CATEGORY_LABEL[category];
  if (known) return known;
  return category.split(/[_\s-]+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
function isTriggerStep(meta) {
  return meta.category === StepCategory.TRIGGERS || meta.name === "trigger_evaluate";
}
function isJsonOptionType(type) {
  return type === "object" || type === "array" || type === "json";
}
function stepRefId(meta) {
  return `${meta.name}@${meta.version}`;
}
function isEnrichmentStep(meta) {
  return meta.category === "enrich" || meta.name.startsWith("enrich_") || meta.name.endsWith("_enrich");
}
function defaultStepMetadata(partial) {
  return {
    version: "0.1.0",
    owner: { team: "unassigned" },
    supportedTiers: [],
    needs: [],
    failurePolicy: FailurePolicy.FAIL_STEP,
    retryPolicy: { ...DEFAULT_RETRY_POLICY },
    requiredFlags: [],
    options: [],
    category: StepCategory.CUSTOM,
    ...partial
  };
}

// src/pipeline/outcome.ts
var StepDecisionReason = {
  RAN: "ran",
  FEATURE_FLAG_OFF: "feature_flag_off",
  TIER_UNSUPPORTED: "tier_unsupported",
  UPSTREAM_FAILED: "upstream_failed",
  UPSTREAM_SKIPPED: "upstream_skipped",
  IDEMPOTENCY_HIT: "idempotency_hit",
  DEPENDENCY_NOT_WIRED: "dependency_not_wired",
  NO_INPUT: "no_input",
  POLICY_DISABLED: "policy_disabled",
  QUOTA_EXCEEDED: "quota_exceeded",
  /** The payer's PLAN does not include the lane — not the run's own config. */
  ENTITLEMENT_MISSING: "entitlement_missing",
  NOTHING_TO_DO: "nothing_to_do",
  FAILED: "failed"
};
var SKIP_REASONS = /* @__PURE__ */ new Set([
  StepDecisionReason.FEATURE_FLAG_OFF,
  StepDecisionReason.TIER_UNSUPPORTED,
  StepDecisionReason.UPSTREAM_FAILED,
  StepDecisionReason.UPSTREAM_SKIPPED,
  StepDecisionReason.IDEMPOTENCY_HIT,
  StepDecisionReason.DEPENDENCY_NOT_WIRED,
  StepDecisionReason.NO_INPUT,
  StepDecisionReason.POLICY_DISABLED,
  StepDecisionReason.ENTITLEMENT_MISSING
]);
function isSkipReason(reason) {
  return reason !== void 0 && SKIP_REASONS.has(reason);
}
function isSkipped(outcome) {
  return outcome.status === "skipped" || isSkipReason(outcome.decision?.reason) || outcome.attempts === 0 || (outcome.error?.startsWith("skipped:") ?? false);
}
function outcomeReason(outcome) {
  const decision = outcome.decision;
  if (decision && decision.reason !== StepDecisionReason.RAN) {
    return decision.summary || decision.reason;
  }
  return outcome.error ?? void 0;
}
function summarizeOutcomes(outcomes) {
  let ok = 0;
  let degraded = 0;
  let failed = 0;
  let skipped = 0;
  for (const o of outcomes) {
    if (isSkipped(o)) {
      skipped += 1;
      continue;
    }
    if (o.status === "failed") failed += 1;
    else if (o.status === "degraded") degraded += 1;
    else ok += 1;
  }
  return { ok, degraded, failed, skipped };
}

// src/pipeline/document.ts
var BODY_SCOPES = ["customer", "org", "tenant", "user"];
var DEFAULT_BODY_SCOPE = "tenant";
function isNodeEnabled(node) {
  return node.enabled !== false;
}
function newNodeId(prefix = "node") {
  return `${prefix}_${Date.now().toString(36)}_${randomHex(8)}`;
}
function createPipelineDoc(partial = {}) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    id: partial.id ?? newNodeId("pipeline"),
    name: partial.name ?? "Untitled pipeline",
    docVersion: partial.docVersion,
    description: partial.description,
    targetTier: partial.targetTier ?? LatencyTier.CONFIRMED,
    dynamicStepBudget: partial.dynamicStepBudget ?? 8,
    parallelExecution: partial.parallelExecution ?? false,
    nodes: partial.nodes ?? [],
    // No flags pinned by default. A flag the doc does not name resolves from the
    // platform's settings at run time; pinning `tx_graph: true` on every new doc
    // overrode that and made two otherwise-identical pipelines hash differently.
    // A template that needs a flag carries it (`PipelineTemplate.featureFlags`).
    featureFlags: partial.featureFlags ?? {},
    // Left unset by default: the backend already defaults the body lane to the
    // shared tenant scope ({@link DEFAULT_BODY_SCOPE}), so an unset value avoids
    // writing a redundant `config.persistence` overlay. Set explicitly (e.g. via
    // the builder toolbar) to opt into `user`-private bodies or widen to org /
    // customer.
    bodyScope: partial.bodyScope,
    configOverlay: partial.configOverlay,
    scope: partial.scope,
    templateId: partial.templateId,
    createdAt: partial.createdAt ?? now,
    updatedAt: partial.updatedAt ?? now,
    workspaceId: partial.workspaceId,
    tenantId: partial.tenantId
  };
}
function duplicatePipelineName(name, opts = {}) {
  const base = (opts.system ? name.replace(/^\s*System default\s*[—–-]\s*/i, "") : name).trim();
  return `${base || "Untitled pipeline"} (copy)`;
}
function duplicatePipelineDoc(doc, opts = {}) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    ...doc,
    id: newNodeId("pipeline"),
    name: opts.name ?? duplicatePipelineName(doc.name, { system: opts.system }),
    // A preset's description states the PRESET's role — the platform's
    // "System-wide default: ingest, normalize, decode…" — and a copy that kept
    // it carried that claim into every picker as its own subtitle, so a
    // tenant's throwaway pipeline read as the system default where people
    // choose what to run. A copy of a user's own pipeline keeps its author's
    // words, which still describe it.
    description: opts.system ? `Your copy of the ${(opts.sourceName ?? "").trim() || "platform"} preset.` : doc.description,
    docVersion: void 0,
    nodes: doc.nodes.map((node) => ({
      ...node,
      needs: [...node.needs],
      options: structuredClone(node.options),
      position: { ...node.position },
      ...node.ui ? { ui: structuredClone(node.ui) } : {}
    })),
    featureFlags: doc.featureFlags ? { ...doc.featureFlags } : doc.featureFlags,
    configOverlay: doc.configOverlay ? structuredClone(doc.configOverlay) : void 0,
    createdAt: now,
    updatedAt: now,
    workspaceId: void 0,
    tenantId: void 0
  };
}
function uniqueNodeName(doc, base) {
  const taken = new Set(doc.nodes.map((n) => n.nodeName));
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
}
function downstreamOf(doc, nodeName) {
  const dependents = /* @__PURE__ */ new Map();
  for (const n of doc.nodes) {
    for (const dep of n.needs) {
      const list = dependents.get(dep);
      if (list) list.push(n.nodeName);
      else dependents.set(dep, [n.nodeName]);
    }
  }
  const out = /* @__PURE__ */ new Set();
  const queue = [...dependents.get(nodeName) ?? []];
  while (queue.length > 0) {
    const next = queue.pop();
    if (out.has(next) || next === nodeName) continue;
    out.add(next);
    queue.push(...dependents.get(next) ?? []);
  }
  return out;
}
function wouldCreateCycle(doc, source, target) {
  return source === target || downstreamOf(doc, target).has(source);
}
function topoSort(doc) {
  const byName = /* @__PURE__ */ new Map();
  for (const n of doc.nodes) byName.set(n.nodeName, n);
  const visited = /* @__PURE__ */ new Set();
  const temp = /* @__PURE__ */ new Set();
  const order = [];
  let cycle;
  const stack = [];
  const visit = (name) => {
    if (visited.has(name)) return true;
    if (temp.has(name)) {
      const idx = stack.indexOf(name);
      cycle = stack.slice(idx >= 0 ? idx : 0).concat(name);
      return false;
    }
    temp.add(name);
    stack.push(name);
    const node = byName.get(name);
    if (node) {
      for (const dep of node.needs) {
        if (!byName.has(dep)) continue;
        if (!visit(dep)) return false;
      }
    }
    stack.pop();
    temp.delete(name);
    visited.add(name);
    order.push(name);
    return true;
  };
  for (const n of doc.nodes) {
    if (!visit(n.nodeName)) return { order: [], cycle };
  }
  return { order };
}
function validatePipelineDoc(doc, options = {}) {
  const issues = [];
  if (doc.nodes.length === 0) {
    issues.push({ level: "warning", code: "empty_pipeline", message: "Pipeline has no steps yet." });
  }
  if (!LATENCY_TIERS.includes(String(doc.targetTier ?? "").trim().toLowerCase())) {
    issues.push({
      level: "error",
      code: "invalid_target_tier",
      message: `Unknown target tier "${String(doc.targetTier)}"; expected one of ${LATENCY_TIERS.join(", ")}.`,
      field: "target_tier"
    });
  }
  const seen = /* @__PURE__ */ new Set();
  const seenIds = /* @__PURE__ */ new Set();
  for (const node of doc.nodes) {
    if (node.nodeName.trim() === "") {
      issues.push({
        level: "error",
        code: "empty_node_name",
        message: `Step "${node.stepName}" has an empty node name.`,
        nodeId: node.id
      });
    }
    if (seen.has(node.nodeName)) {
      issues.push({
        level: "error",
        code: "duplicate_node_name",
        message: `Duplicate node name "${node.nodeName}".`,
        nodeId: node.id
      });
    }
    seen.add(node.nodeName);
    if (seenIds.has(node.id)) {
      issues.push({
        level: "error",
        code: "duplicate_node_id",
        message: `Duplicate node id "${node.id}" \u2014 "${node.nodeName}" shares it with another step.`,
        nodeId: node.id
      });
    }
    seenIds.add(node.id);
  }
  const byName = new Map(doc.nodes.map((n) => [n.nodeName, n]));
  for (const node of doc.nodes) {
    if (!isNodeEnabled(node)) continue;
    for (const dep of node.needs) {
      const upstream = byName.get(dep);
      if (!upstream) {
        issues.push({
          level: "error",
          code: "dangling_edge",
          message: `"${node.nodeName}" needs "${dep}" which is not in this pipeline.`,
          nodeId: node.id
        });
      } else if (!isNodeEnabled(upstream)) {
        issues.push({
          level: "warning",
          code: "disabled_edge",
          message: `"${node.nodeName}" needs "${dep}", which is disabled; the edge is dropped at compile time.`,
          nodeId: node.id
        });
      }
    }
  }
  if (doc.nodes.filter(isNodeEnabled).length > 1) {
    const depended = /* @__PURE__ */ new Set();
    for (const node of doc.nodes) {
      if (!isNodeEnabled(node)) continue;
      for (const dep of node.needs) depended.add(dep);
    }
    for (const node of doc.nodes) {
      if (!isNodeEnabled(node)) continue;
      if (node.needs.length > 0 || depended.has(node.nodeName)) continue;
      issues.push({
        level: "warning",
        code: "disconnected_node",
        message: `"${node.nodeName}" is not wired to the pipeline \u2014 nothing feeds it and nothing uses it. Give it a dependency, or remove it.`,
        nodeId: node.id
      });
    }
  }
  const { cycle } = topoSort(doc);
  if (cycle && cycle.length > 0) {
    const flow = [...cycle].reverse();
    const first = doc.nodes.find((n) => n.nodeName === flow[0]);
    issues.push({
      level: "error",
      code: "compile_error",
      message: `Pipeline has a cycle: ${flow.join(" \u2192 ")}. Remove one of these dependencies.`,
      // The canvas marks the node an issue names; a cycle named no node, so
      // "1 error" had no place on the DAG to point at.
      ...first ? { nodeId: first.id } : {}
    });
  }
  if (doc.dynamicStepBudget < 0) {
    issues.push({
      level: "error",
      code: "invalid_dynamic_step_budget",
      message: "Dynamic-step budget must be \u2265 0."
    });
  }
  const lookup = options.lookup;
  if (lookup) {
    for (const node of doc.nodes) {
      if (!isNodeEnabled(node)) continue;
      const meta = lookup(node.stepName, node.stepVersion);
      if (!meta) {
        const carried = options.versionsOf?.(node.stepName) ?? [];
        const hint = carried.length > 0 ? ` The registry carries ${carried.map((v) => `${node.stepName}@${v}`).join(", ")} \u2014 re-pin the node.` : "";
        issues.push(
          options.strict ? {
            level: "error",
            code: "unknown_step",
            message: `Step ${node.stepName}@${node.stepVersion} is not registered.${hint}`,
            nodeId: node.id,
            field: "step_version"
          } : {
            level: "warning",
            code: "unknown_step",
            message: `Step ${node.stepName}@${node.stepVersion} is not in the loaded registry \u2014 the server will reject it unless the catalog is stale.${hint}`,
            nodeId: node.id,
            field: "step_name"
          }
        );
        continue;
      }
      for (const opt of meta.options) {
        if (opt.required && !(opt.name in node.options)) {
          issues.push({
            level: "error",
            code: "missing_required_option",
            message: `Step ${node.stepName} requires option "${opt.name}".`,
            nodeId: node.id,
            field: opt.name
          });
        }
      }
      if (meta.runtimeDeps !== void 0) {
        const accepted = /* @__PURE__ */ new Set([...meta.options.map((o) => o.name), ...meta.runtimeDeps]);
        for (const key of Object.keys(node.options)) {
          if (accepted.has(key)) continue;
          issues.push({
            level: "error",
            code: "unknown_option",
            message: `Step ${node.stepName} does not accept option "${key}"; constructing it would fail every run.`,
            nodeId: node.id,
            field: key
          });
        }
      }
      if (meta.supportedTiers.length > 0 && !meta.supportedTiers.includes(doc.targetTier)) {
        issues.push({
          level: "warning",
          code: "tier_unsupported",
          message: `Step ${node.stepName} does not support tier "${doc.targetTier}"; it will be skipped at run time.`,
          nodeId: node.id
        });
      }
      for (const flag of meta.requiredFlags) {
        if (doc.featureFlags?.[flag] === false) {
          issues.push({
            level: "warning",
            code: "feature_flag_off",
            message: `Step ${node.stepName} requires feature flag "${flag}", which this pipeline disables; it will be skipped at run time.`,
            nodeId: node.id
          });
        }
      }
    }
  }
  return issues;
}
function isDeployablePipeline(doc, options = {}) {
  return validatePipelineDoc(doc, options).every((i) => i.level !== "error");
}
function findNode(doc, nodeId) {
  return doc.nodes.find((n) => n.id === nodeId);
}

// src/pipeline/docConfig.ts
var KNOWN_DOC_CONFIG_SECTIONS = [
  "batch",
  "decode_reference_revision",
  "dust_campaign",
  "enrichment",
  "enterprise_reconciliation",
  "evidence",
  "evidence_activity",
  "execution",
  "feature_flags",
  "flow_chain",
  "graph",
  "grouping",
  "idempotency_ttl_seconds_by_tier",
  "index_projections",
  "metadata_registry",
  "persistence",
  "pool_lifecycle",
  "prediction_market",
  "prediction_record",
  "protocol_detection",
  "protocol_overrides",
  "risk_pattern",
  "summaries",
  "transactions_table_name",
  "tx_quality",
  "tx_semantic"
];
var KNOWN = new Set(KNOWN_DOC_CONFIG_SECTIONS);
function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function asBodyScope(value) {
  const s = String(value ?? "").trim().toLowerCase();
  return BODY_SCOPES.includes(s) ? s : void 0;
}
function docConfigOf(doc) {
  const out = structuredClone(doc.configOverlay ?? {});
  if (doc.featureFlags && Object.keys(doc.featureFlags).length > 0) {
    out.feature_flags = { ...doc.featureFlags };
  }
  if (doc.bodyScope) {
    const persistence = isPlainObject(out.persistence) ? out.persistence : {};
    out.persistence = { ...persistence, body_scope: doc.bodyScope };
  }
  return out;
}
function withDocConfig(doc, config) {
  const rest = structuredClone(config ?? {});
  let featureFlags = {};
  if (isPlainObject(rest.feature_flags)) {
    featureFlags = { ...rest.feature_flags };
    delete rest.feature_flags;
  }
  let bodyScope;
  if (isPlainObject(rest.persistence)) {
    const persistence = { ...rest.persistence };
    if ("body_scope" in persistence) {
      bodyScope = asBodyScope(persistence.body_scope);
      if (bodyScope !== void 0) delete persistence.body_scope;
    }
    if (Object.keys(persistence).length > 0) rest.persistence = persistence;
    else delete rest.persistence;
  }
  return {
    ...doc,
    featureFlags,
    bodyScope,
    configOverlay: Object.keys(rest).length > 0 ? rest : void 0
  };
}
var FILTER_INDEXES_CONFIG_KEY = "filter_indexes";
function filterIndexesOf(config) {
  const persistence = config?.persistence;
  if (!isPlainObject(persistence)) return void 0;
  const value = persistence[FILTER_INDEXES_CONFIG_KEY];
  return typeof value === "boolean" ? value : void 0;
}
function withFilterIndexes(config, value) {
  const out = structuredClone(config ?? {});
  if (value === void 0 && out.persistence !== void 0 && !isPlainObject(out.persistence)) return out;
  const persistence = isPlainObject(out.persistence) ? { ...out.persistence } : {};
  if (value === void 0) delete persistence[FILTER_INDEXES_CONFIG_KEY];
  else persistence[FILTER_INDEXES_CONFIG_KEY] = value;
  if (Object.keys(persistence).length > 0) out.persistence = persistence;
  else delete out.persistence;
  return out;
}
function unknownDocConfigSections(config) {
  return Object.keys(config).filter((key) => !KNOWN.has(key)).sort();
}

// src/pipeline/presentation.ts
function stepDisplayName(name) {
  const display = customerText(name);
  if (display === name) return name;
  const normalizer = name.replace(/^normalize_moralis(?=_\d+$|$)/, "normalize_transaction").replace(/^normalize_quicknode(?=_\d+$|$)/, "normalize_rpc_transaction");
  return normalizer !== name ? normalizer : display;
}
function nodeDisplayName(node) {
  if (node.nodeName === node.stepName || node.nodeName.startsWith(`${node.stepName}_`)) {
    return stepDisplayName(node.stepName) + node.nodeName.slice(node.stepName.length);
  }
  return node.nodeName;
}

// src/pipeline/mermaid.ts
function mermaidIds(doc) {
  const byNodeId = /* @__PURE__ */ new Map();
  const byName = /* @__PURE__ */ new Map();
  const taken = /* @__PURE__ */ new Set();
  for (const node of doc.nodes) {
    const base = `n_${nodeDisplayName(node).replace(/[^A-Za-z0-9_]/g, "_")}`;
    let id = base;
    let i = 2;
    while (taken.has(id)) {
      id = `${base}_${i}`;
      i += 1;
    }
    taken.add(id);
    byNodeId.set(node.id, id);
    if (!byName.has(node.nodeName)) byName.set(node.nodeName, id);
  }
  return { byNodeId, byName };
}
function escapeLabel(text) {
  return text.replace(/"/g, "&quot;");
}
function mermaidLabel(node) {
  return escapeLabel(`${nodeDisplayName(node)}<br/>step: ${stepDisplayName(node.stepName)}@${node.stepVersion}`);
}
function renderPipelineMermaid(doc, options = {}) {
  const direction = options.direction ?? "TD";
  const lines = [`flowchart ${direction}`];
  const { byNodeId, byName } = mermaidIds(doc);
  const enrichIds = [];
  for (const node of doc.nodes) {
    const id = byNodeId.get(node.id);
    if (id === void 0) continue;
    lines.push(`    ${id}["${mermaidLabel(node)}"]`);
    const meta = options.catalog?.get(`${node.stepName}@${node.stepVersion}`);
    const enrich = meta ? isEnrichmentStep(meta) : node.stepName.startsWith("enrich_") || node.stepName.endsWith("_enrich");
    if (enrich) enrichIds.push(id);
  }
  for (const node of doc.nodes) {
    const targetId = byNodeId.get(node.id);
    if (targetId === void 0) continue;
    for (const dep of node.needs) {
      const sourceId = byName.get(dep);
      if (sourceId === void 0) continue;
      lines.push(`    ${sourceId} --> ${targetId}`);
    }
  }
  if (enrichIds.length > 0) {
    lines.push("    classDef enrich fill:#fef3c7,stroke:#d97706,color:#92400e;");
    lines.push(`    class ${enrichIds.join(",")} enrich;`);
  }
  return lines.join("\n");
}

// src/pipeline/wire.ts
function pick(raw, ...keys) {
  for (const key of keys) {
    if (raw[key] !== void 0 && raw[key] !== null) return raw[key];
  }
  return void 0;
}
function asTier(value) {
  const tier = String(value ?? "").toLowerCase();
  return LATENCY_TIER_VALUES.includes(tier) ? tier : LatencyTier.CONFIRMED;
}
var LATENCY_TIER_VALUES = Object.values(LatencyTier);
function asSupportedTiers(values) {
  const known = new Set(values.map((v) => String(v ?? "").toLowerCase()));
  return LATENCY_TIERS.filter((t) => known.has(t));
}
function docTierFromWire(value) {
  if (value === void 0 || value === null || value === "") return LatencyTier.CONFIRMED;
  return String(value);
}
var OPTION_TYPES = /* @__PURE__ */ new Set([
  "string",
  "number",
  "boolean",
  "object",
  "array",
  "json"
]);
function asOptionType(value) {
  const t = String(value ?? "string").toLowerCase();
  return OPTION_TYPES.has(t) ? t : "json";
}
function retryPolicyFromWire(raw) {
  if (!raw || typeof raw !== "object") return void 0;
  const w = raw;
  const num2 = (v, fallback) => typeof v === "number" && Number.isFinite(v) ? v : fallback;
  return {
    maxAttempts: num2(pick(w, "max_attempts", "maxAttempts"), DEFAULT_RETRY_POLICY.maxAttempts),
    initialBackoffSeconds: num2(
      pick(w, "initial_backoff_seconds", "initialBackoffSeconds"),
      DEFAULT_RETRY_POLICY.initialBackoffSeconds
    ),
    backoffMultiplier: num2(pick(w, "backoff_multiplier", "backoffMultiplier"), DEFAULT_RETRY_POLICY.backoffMultiplier),
    maxBackoffSeconds: num2(pick(w, "max_backoff_seconds", "maxBackoffSeconds"), DEFAULT_RETRY_POLICY.maxBackoffSeconds)
  };
}
function asFailurePolicy(value) {
  const fp = String(value ?? "").toLowerCase();
  return Object.values(FailurePolicy).includes(fp) ? fp : FailurePolicy.FAIL_STEP;
}
function asStatus(value) {
  const s = String(value ?? "").toLowerCase();
  return Object.values(StepStatus).includes(s) ? s : StepStatus.OK;
}
function docToWire(doc) {
  const idByName = new Map(doc.nodes.map((n) => [n.nodeName, n.id]));
  return {
    id: doc.id,
    name: doc.name,
    description: doc.description ?? null,
    // The backend revision, as read. The API bumps it on update and refuses an
    // update older than what it holds (409), so echoing what we loaded is what
    // makes a stale tab's save fail loudly instead of overwriting a newer one.
    // It is also the pipeline's version: each save is kept as that revision.
    doc_version: doc.docVersion ?? 1,
    target_tier: doc.targetTier,
    parallel_execution: doc.parallelExecution,
    dynamic_step_budget: doc.dynamicStepBudget,
    nodes: doc.nodes.map((n) => nodeToWire(n, idByName)),
    // `doc.config` — the one config layer the backend reads from a doc. It is
    // the BASE of the run's config: the compiler folds it in as the global layer
    // of the resolved config, and the platform's own settings (AppConfig, the
    // run request's overlay) are resolved around it, not underneath it — a flag
    // named here is what this pipeline asks for, not an override of anything.
    // `docConfigOf` merges the modelled sections (`feature_flags`,
    // `persistence.body_scope`) back into the verbatim overlay, so sections the
    // builder has no editor for survive the round trip.
    config: docConfigOf(doc),
    metadata: {
      scope: doc.scope ?? null,
      created_at: doc.createdAt ?? null,
      updated_at: doc.updatedAt ?? null,
      workspace_id: doc.workspaceId ?? null,
      tenant_id: doc.tenantId ?? null,
      // The template this pipeline was instantiated from — lets the run console
      // surface that template's ready-made run samples. Omitted when absent.
      ...doc.templateId ? { template_id: doc.templateId } : {}
      // No `hierarchical_config` and no `injections`: both used to ride here and
      // nothing on the backend ever read either, so what the Config tab and the
      // "Step injection" panel authored saved, reloaded and did nothing.
    }
  };
}
function nodeToWire(node, idByName) {
  return {
    id: node.id,
    name: node.nodeName,
    step_name: node.stepName,
    step_version: node.stepVersion,
    // Edges out by node id (fall back to the raw value so a genuinely dangling
    // dep still surfaces server-side rather than being silently dropped).
    needs: node.needs.map((dep) => idByName.get(dep) ?? dep),
    options: node.options,
    // As authored. This was a literal `true`, which re-enabled every node a
    // preset ships disabled the moment a copy of it was saved.
    enabled: node.enabled !== false,
    // Extra hints (drafts, collapse) first, so the position always wins.
    ui: { ...node.ui ?? {}, x: node.position.x, y: node.position.y }
  };
}
function docFromWire(raw) {
  const metadata = raw.metadata ?? {};
  const rawNodes = pick(raw, "nodes") ?? [];
  const nameById = /* @__PURE__ */ new Map();
  for (const rn of rawNodes) {
    const id = pick(rn, "id");
    if (id === void 0) continue;
    nameById.set(id, pick(rn, "name", "nodeName") ?? pick(rn, "step_name", "stepName") ?? id);
  }
  const config = pick(raw, "config") ?? {};
  const configured = withDocConfig({}, config);
  const docVersion = pick(raw, "doc_version", "docVersion");
  return createPipelineDoc({
    id: pick(raw, "id"),
    name: pick(raw, "name"),
    docVersion: typeof docVersion === "number" && Number.isFinite(docVersion) ? docVersion : void 0,
    description: pick(raw, "description") ?? void 0,
    targetTier: docTierFromWire(pick(raw, "target_tier", "targetTier")),
    dynamicStepBudget: pick(raw, "dynamic_step_budget", "dynamicStepBudget") ?? 8,
    parallelExecution: pick(raw, "parallel_execution", "parallelExecution") ?? false,
    featureFlags: configured.featureFlags ?? {},
    bodyScope: configured.bodyScope,
    configOverlay: configured.configOverlay,
    nodes: rawNodes.map((rn) => nodeFromWire(rn, nameById)),
    scope: pick(metadata, "scope") ?? pick(raw, "scope"),
    templateId: pick(metadata, "template_id", "templateId") ?? pick(raw, "template_id", "templateId"),
    createdAt: pick(metadata, "created_at") ?? pick(raw, "created_at", "createdAt"),
    updatedAt: pick(metadata, "updated_at") ?? pick(raw, "updated_at", "updatedAt"),
    workspaceId: pick(metadata, "workspace_id") ?? pick(raw, "workspace_id", "workspaceId"),
    tenantId: pick(metadata, "tenant_id") ?? pick(raw, "tenant_id", "tenantId")
  });
}
function nodeFromWire(raw, nameById) {
  const stepName = pick(raw, "step_name", "stepName") ?? "";
  const ui = pick(raw, "ui") ?? {};
  const pos = pick(raw, "position") ?? { x: ui.x ?? 0, y: ui.y ?? 0 };
  const rawNeeds = pick(raw, "needs") ?? [];
  const { x: _x, y: _y, ...uiRest } = ui;
  const node = {
    // A minted id, not the step name: two nodes running the same step in a
    // payload whose nodes carry no id (a hand seed / pre-cutover doc) both
    // landed on the same canvas id, and one id means one node — selecting,
    // moving or deleting either hit both.
    id: pick(raw, "id") ?? newNodeId(),
    nodeName: pick(raw, "name", "nodeName") ?? stepName,
    stepName,
    stepVersion: pick(raw, "step_version", "stepVersion") ?? "0.1.0",
    needs: rawNeeds.map((dep) => nameById.get(dep) ?? dep),
    options: pick(raw, "options") ?? {},
    position: { x: Number(pos.x ?? 0), y: Number(pos.y ?? 0) }
  };
  if (raw.enabled === false) node.enabled = false;
  if (Object.keys(uiRest).length > 0) node.ui = uiRest;
  return node;
}
function stepMetadataFromWire(raw) {
  const modelBinding = raw.model_binding && typeof raw.model_binding === "object" && !Array.isArray(raw.model_binding) ? raw.model_binding : null;
  const meta = {
    name: pick(raw, "name") ?? "",
    version: pick(raw, "version") ?? "0.1.0",
    owner: {
      team: pick(raw, "owner_team", "ownerTeam") ?? "unassigned",
      description: pick(raw, "description") ?? void 0
    },
    supportedTiers: asSupportedTiers(pick(raw, "supported_tiers", "supportedTiers") ?? []),
    needs: pick(raw, "needs") ?? [],
    failurePolicy: asFailurePolicy(pick(raw, "failure_policy", "failurePolicy")),
    // The runner's own policy when the catalog carries it (`Step.retry_policy`);
    // the documented default otherwise, rather than a guess dressed as a fact.
    retryPolicy: retryPolicyFromWire(pick(raw, "retry_policy", "retryPolicy")) ?? { ...DEFAULT_RETRY_POLICY },
    requiredFlags: pick(raw, "required_flags", "requiredFlags") ?? [],
    ...modelBinding ? { modelBinding: {
      name: pick(modelBinding, "name") ?? null,
      featureSchema: pick(modelBinding, "feature_schema") ?? null,
      enrichmentKind: pick(modelBinding, "enrichment_kind") ?? null
    } } : {},
    options: (pick(raw, "options") ?? []).map((o) => {
      const choices = pick(o, "enum_values", "choices");
      return {
        name: pick(o, "name") ?? "",
        type: asOptionType(pick(o, "type")),
        required: pick(o, "required") ?? false,
        default: pick(o, "default"),
        description: pick(o, "description") || void 0,
        // `[]` is the backend's "no enum" — not a select with nothing to pick.
        choices: choices && choices.length > 0 ? choices : void 0
      };
    }),
    // The backend's category, passed through (legacy FE spellings are aliased).
    category: normalizeStepCategory(pick(raw, "category")),
    custom: pick(raw, "custom") ?? false,
    runtimeId: pick(raw, "code_runtime_id", "runtimeId") ?? void 0
  };
  const runtimeDeps = pick(raw, "runtime_deps", "runtimeDeps");
  if (Array.isArray(runtimeDeps)) meta.runtimeDeps = [...runtimeDeps];
  return meta;
}
function catalogFromWire(raw) {
  const steps = pick(raw, "steps") ?? (Array.isArray(raw) ? raw : []);
  return steps.map(stepMetadataFromWire);
}
function templateNodeFromWire(raw) {
  const node = {
    stepName: pick(raw, "step_name", "stepName") ?? ""
  };
  const stepVersion = pick(raw, "step_version", "stepVersion");
  if (stepVersion) node.stepVersion = stepVersion;
  const nodeName = pick(raw, "node_name", "nodeName");
  if (nodeName) node.nodeName = nodeName;
  const needs = pick(raw, "needs");
  if (needs && needs.length > 0) node.needs = needs;
  const options = pick(raw, "options");
  if (options && Object.keys(options).length > 0) node.options = options;
  return node;
}
function txSpecFromWire(raw) {
  const spec = {
    chainId: pick(raw, "chain_id", "chainId") ?? "",
    txHash: pick(raw, "tx_hash", "txHash") ?? ""
  };
  const label = pick(raw, "label");
  if (label) spec.label = label;
  return spec;
}
function sampleFromWire(raw) {
  const sample = {
    id: pick(raw, "id") ?? "",
    name: pick(raw, "name") ?? "",
    sourceKind: pick(raw, "source_kind", "sourceKind") ?? "tx_hashes"
  };
  const description = pick(raw, "description");
  if (description) sample.description = description;
  const txSpecs = pick(raw, "tx_specs", "txSpecs");
  if (txSpecs && txSpecs.length > 0) sample.txSpecs = txSpecs.map(txSpecFromWire);
  const chainId = pick(raw, "chain_id", "chainId");
  if (chainId) sample.chainId = chainId;
  const txHashes = pick(raw, "tx_hashes", "txHashes");
  if (txHashes && txHashes.length > 0) sample.txHashes = txHashes;
  const address = pick(raw, "address");
  if (address) sample.address = address;
  const selector = pick(raw, "selector");
  if (selector) sample.selector = selector;
  const transactionTypes = pick(raw, "transaction_types", "transactionTypes");
  if (transactionTypes) sample.transactionTypes = transactionTypes;
  const count = pick(raw, "count");
  if (count !== void 0) sample.count = count;
  return sample;
}
function templateFromWire(raw) {
  const tmpl = {
    id: pick(raw, "id") ?? "",
    name: pick(raw, "name") ?? "",
    description: pick(raw, "description") ?? "",
    nodes: (pick(raw, "nodes") ?? []).map(templateNodeFromWire)
  };
  const category = pick(raw, "category");
  if (category) tmpl.category = category;
  const targetTier = pick(raw, "target_tier", "targetTier");
  if (targetTier !== void 0) tmpl.targetTier = asTier(targetTier);
  const budget = pick(raw, "dynamic_step_budget", "dynamicStepBudget");
  if (budget !== void 0) tmpl.dynamicStepBudget = budget;
  const flags = pick(raw, "feature_flags", "featureFlags");
  if (flags && Object.keys(flags).length > 0) tmpl.featureFlags = flags;
  const config = pick(raw, "config");
  if (config && Object.keys(config).length > 0) tmpl.config = config;
  const samples = pick(raw, "samples");
  if (samples && samples.length > 0) tmpl.samples = samples.map(sampleFromWire);
  return tmpl;
}
function templatesFromWire(raw) {
  const list = Array.isArray(raw) ? raw : pick(raw, "templates") ?? [];
  return list.map(templateFromWire);
}
var ISSUE_LEVELS = /* @__PURE__ */ new Set(["error", "warning", "info"]);
function issueFromWire(raw) {
  const level = String(pick(raw, "level") ?? "error").toLowerCase();
  const issue = {
    level: ISSUE_LEVELS.has(level) ? level : "error",
    message: pick(raw, "message") ?? "",
    nodeId: pick(raw, "node_id", "nodeId") ?? void 0
  };
  const code = pick(raw, "code");
  if (code) issue.code = code;
  const field = pick(raw, "field");
  if (field) issue.field = field;
  return issue;
}
function issuesFromWire(raw) {
  const list = Array.isArray(raw) ? raw : raw.issues ?? [];
  return list.map(issueFromWire);
}
function decisionFromWire(raw) {
  if (!raw || typeof raw !== "object") return void 0;
  const w = raw;
  const reason = pick(w, "reason");
  if (reason === void 0) return void 0;
  return {
    reason,
    summary: pick(w, "summary") ?? "",
    detail: pick(w, "detail") ?? {}
  };
}
function outcomeFromWire(raw) {
  const outcome = {
    nodeName: pick(raw, "node_name", "nodeName") ?? "",
    stepName: pick(raw, "step_name", "stepName") ?? "",
    stepVersion: pick(raw, "step_version", "stepVersion") ?? "",
    status: asStatus(pick(raw, "status")),
    error: pick(raw, "error") ?? null,
    enrichmentCount: pick(raw, "enrichment_count", "enrichmentCount") ?? 0,
    attempts: pick(raw, "attempts") ?? 1,
    nextSteps: []
  };
  const durationMs = pick(raw, "duration_ms", "durationMs");
  if (typeof durationMs === "number" && Number.isFinite(durationMs)) {
    outcome.durationMs = durationMs;
  }
  const decision = decisionFromWire(raw.decision) ?? (pick(raw, "decision_reason", "decisionReason") !== void 0 ? {
    reason: pick(raw, "decision_reason", "decisionReason"),
    summary: pick(raw, "decision_summary", "decisionSummary") ?? "",
    detail: pick(raw, "decision_detail", "decisionDetail") ?? {}
  } : void 0);
  if (decision !== void 0) outcome.decision = decision;
  return outcome;
}
function runResultFromWire(raw) {
  const outcomes = (pick(raw, "outcomes") ?? []).map(outcomeFromWire);
  return {
    outcomes,
    dynamicStepsRun: pick(raw, "dynamic_steps_run", "dynamicStepsRun") ?? 0,
    output: raw
  };
}

// src/pipeline/step-catalog.generated.json
var step_catalog_generated_default = {
  steps: [
    {
      category: "decode",
      code_runtime_id: null,
      custom: false,
      description: "Decode transaction calldata + events via the decode_transaction package.",
      failure_policy: "degrade",
      model_binding: null,
      name: "decode",
      needs: [
        "normalize_moralis"
      ],
      options: [
        {
          default: null,
          description: "",
          enum_values: [],
          name: "decode_options",
          required: false,
          type: "object"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "decode_input",
          required: false,
          type: "boolean"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "decode_output",
          required: false,
          type: "boolean"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "decode_events",
          required: false,
          type: "boolean"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "decode_internal_txs",
          required: false,
          type: "boolean"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "guess_abi",
          required: false,
          type: "boolean"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "follow_proxy",
          required: false,
          type: "boolean"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "use_default_abis",
          required: false,
          type: "boolean"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "fetch_abi_from_s3",
          required: false,
          type: "boolean"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "build_semantic",
          required: false,
          type: "boolean"
        },
        {
          default: false,
          description: "",
          enum_values: [],
          name: "emit_metrics",
          required: false,
          type: "boolean"
        },
        {
          default: false,
          description: "",
          enum_values: [],
          name: "nested_semantic",
          required: false,
          type: "boolean"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "proxy_onchain_rpc",
          required: false,
          type: "boolean"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "proxy_onchain_rpc_chains",
          required: false,
          type: "object"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "proxy_per_strategy_timeout_seconds",
          required: false,
          type: "number"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "bytecode_analysis",
          required: false,
          type: "boolean"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "bytecode_analyzers",
          required: false,
          type: "object"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "bytecode_deep",
          required: false,
          type: "boolean"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "forensic_probes",
          required: false,
          type: "boolean"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "forensic_probe_ids",
          required: false,
          type: "object"
        }
      ],
      owner_team: "decoding",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "decoder"
      ],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Decode",
      version: "0.2.0"
    },
    {
      category: "decode",
      code_runtime_id: null,
      custom: false,
      description: "Read DecodeStep's proxy detection and emit linked PROXY_FORWARDER / PROXY_DELEGATEE participants.",
      failure_policy: "degrade",
      model_binding: null,
      name: "parse_proxy",
      needs: [
        "parse_safe_execution"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Parse Proxy",
      version: "0.1.0"
    },
    {
      category: "decode",
      code_runtime_id: null,
      custom: false,
      description: "Recognise Gnosis Safe execTransaction and MultiSend calls and emit one TxParticipant per recipient + the Safe + the executor.",
      failure_policy: "degrade",
      model_binding: null,
      name: "parse_safe_execution",
      needs: [
        "parse_user_ops"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Parse Safe Execution",
      version: "0.1.0"
    },
    {
      category: "decode",
      code_runtime_id: null,
      custom: false,
      description: "Recognise ERC-4337 EntryPoint calls and emit one TxParticipant per user-op sender + bundler + paymaster.",
      failure_policy: "degrade",
      model_binding: null,
      name: "parse_user_ops",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool"
      ],
      title: "Parse User Ops",
      version: "0.3.0"
    },
    {
      category: "enrich",
      code_runtime_id: null,
      custom: false,
      description: "Discover entities / labels / tags from provider raw payloads.",
      failure_policy: "degrade",
      model_binding: null,
      name: "catalog_discover",
      needs: [
        "normalize"
      ],
      options: [
        {
          default: null,
          description: "",
          enum_values: [],
          name: "extractors",
          required: false,
          type: "string"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "discoverers",
          required: false,
          type: "string"
        }
      ],
      owner_team: "catalog",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Catalog Discover",
      version: "0.1.0"
    },
    {
      category: "enrich",
      code_runtime_id: null,
      custom: false,
      description: "Emit address-scope catalog enrichments (labels, entities, tags, groups).",
      failure_policy: "degrade",
      model_binding: null,
      name: "enrich_address_catalog",
      needs: [
        "normalize"
      ],
      options: [],
      owner_team: "catalog",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Enrich Address Catalog",
      version: "0.1.0"
    },
    {
      category: "enrich",
      code_runtime_id: null,
      custom: false,
      description: "Emit tx/event/internal-tx catalog enrichments + decoded-name auto-tags.",
      failure_policy: "degrade",
      model_binding: null,
      name: "enrich_tx_catalog",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "catalog",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Enrich Tx Catalog",
      version: "0.1.0"
    },
    {
      category: "enrich",
      code_runtime_id: null,
      custom: false,
      description: "Attach USD prices to every asset move and produce tx_value_usd rollup.",
      failure_policy: "degrade",
      model_binding: null,
      name: "price_enrich",
      needs: [
        "decode",
        "protocol_detect"
      ],
      options: [],
      owner_team: "prices",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Price Enrich",
      version: "0.1.0"
    },
    {
      category: "enrich",
      code_runtime_id: null,
      custom: false,
      description: "Compare private settlement claims with signed cash and custody statements.",
      failure_policy: "degrade",
      model_binding: null,
      name: "reconcile_enterprise",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Reconcile Enterprise",
      version: "0.1.0"
    },
    {
      category: "enrich",
      code_runtime_id: null,
      custom: false,
      description: "Apply risk policies to produce risk_score enrichments with decomposition.",
      failure_policy: "degrade",
      model_binding: null,
      name: "risk_enrich",
      needs: [
        "enrich_address_catalog"
      ],
      options: [],
      owner_team: "risk",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Risk Enrich",
      version: "0.1.0"
    },
    {
      category: "enrich",
      code_runtime_id: null,
      custom: false,
      description: "Resolve catalog-unknown participants via the third-party facade with cache + quarantine semantics.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "third_party_enrich",
      needs: [
        "normalize"
      ],
      options: [
        {
          default: null,
          description: "",
          enum_values: [],
          name: "providers",
          required: false,
          type: "array"
        },
        {
          default: 25,
          description: "",
          enum_values: [],
          name: "max_addresses_per_tx",
          required: false,
          type: "number"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "skip_known",
          required: false,
          type: "boolean"
        }
      ],
      owner_team: "catalog",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "third_party"
      ],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Third Party Enrich",
      version: "0.1.0"
    },
    {
      category: "finality",
      code_runtime_id: null,
      custom: false,
      description: "Advance the canonical tx's finality based on chain tip.",
      failure_policy: "degrade",
      model_binding: null,
      name: "finality_promote",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "storage",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "calculator"
      ],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Finality Promote",
      version: "0.1.0"
    },
    {
      category: "finality",
      code_runtime_id: null,
      custom: false,
      description: "Detect block-hash divergence and emit CorrectionEvents.",
      failure_policy: "degrade",
      model_binding: null,
      name: "reorg_detector",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "storage",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Reorg Detector",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Void alert history for transactions a reorg orphaned.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "alert_reorg",
      needs: [],
      options: [],
      owner_team: "storage",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Alert Reorg",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Write per-sender dust sends into the Global Context Store so a bulk dusting campaign can be recognised across transactions.",
      failure_policy: "degrade",
      model_binding: null,
      name: "context_dust_write",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "storage",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Context Dust Write",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Write per-wallet value-flow moves into the Global Context Store so a forwarding chain into an exchange can be detected across transactions.",
      failure_policy: "degrade",
      model_binding: null,
      name: "context_flow_write",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "storage",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Context Flow Write",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Write Polymarket share->market pairs to the Context Store and stamp the market onto order fills.",
      failure_policy: "degrade",
      model_binding: null,
      name: "context_market_write",
      needs: [
        "decode"
      ],
      options: [
        {
          default: true,
          description: "",
          enum_values: [],
          name: "fill_facts",
          required: false,
          type: "boolean"
        }
      ],
      owner_team: "storage",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Context Market Write",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Write each wallet's genuine payments and received bait into the Global Context Store so an address-poisoning lookalike can be recognised from its victim's history.",
      failure_policy: "degrade",
      model_binding: null,
      name: "context_poisoning_write",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "storage",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Context Poisoning Write",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Write decoded proxy->impl mappings into the Global Context Store.",
      failure_policy: "degrade",
      model_binding: null,
      name: "context_proxy_write",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "storage",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Context Proxy Write",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Project cross-transaction context signals onto the envelope so trigger rules can match them.",
      failure_policy: "degrade",
      model_binding: null,
      name: "context_signal_project",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "triggers",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Context Signal Project",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: true,
      description: "Run user / 3rd-party / internal code behind a serialisable seam; append-only enrichments, never aborts the pipeline.",
      failure_policy: "degrade",
      model_binding: null,
      name: "custom_step",
      needs: [],
      options: [
        {
          default: null,
          description: "",
          enum_values: [],
          name: "runtime_id",
          required: true,
          type: "string"
        },
        {
          default: "internal",
          description: "",
          enum_values: [],
          name: "kind",
          required: false,
          type: "string"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "code_options",
          required: false,
          type: "object"
        }
      ],
      owner_team: "integrations",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Custom Step",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Resolve token-definition metadata via the token SSOT (write-through) and emit reference-only enrichments + async address-exploration events.",
      failure_policy: "degrade",
      model_binding: null,
      name: "enrich_asset_metadata",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "tokens",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Enrich Asset Metadata",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Classify the normalized action for a completed transaction group.",
      failure_policy: "degrade",
      model_binding: null,
      name: "group_action",
      needs: [
        "group"
      ],
      options: [],
      owner_team: "protocols",
      required_flags: [
        "enable_tx_semantic"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "classifier"
      ],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Group Action",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Discover factory-minted instances (pools, vaults, accounts) from the decode and record them in the context store + catalog.",
      failure_policy: "degrade",
      model_binding: null,
      name: "instance_lifecycle",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "protocols",
      required_flags: [
        "enable_instance_lifecycle"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Instance Lifecycle",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Project the decoded tx into stable path_id-keyed metadata facets (EAV rows) and write them to the metadata-registry sink.",
      failure_policy: "degrade",
      model_binding: null,
      name: "metadata_context",
      needs: [
        "decode"
      ],
      options: [
        {
          default: false,
          description: "",
          enum_values: [],
          name: "require_publication",
          required: false,
          type: "boolean"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "fact_namespaces",
          required: false,
          type: "array"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "write_artifacts",
          required: false,
          type: "boolean"
        }
      ],
      owner_team: "storage",
      required_flags: [
        "enable_metadata_registry"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "normalizer",
        "cpu_runner"
      ],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Metadata Context",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Project the decoded tx into stable path_id-keyed metadata facets (EAV rows) and write them to the metadata-registry sink.",
      failure_policy: "degrade",
      model_binding: null,
      name: "metadata_extract",
      needs: [
        "decode"
      ],
      options: [
        {
          default: false,
          description: "",
          enum_values: [],
          name: "project_only",
          required: false,
          type: "boolean"
        },
        {
          default: false,
          description: "",
          enum_values: [],
          name: "require_publication",
          required: false,
          type: "boolean"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "fact_namespaces",
          required: false,
          type: "array"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "write_artifacts",
          required: false,
          type: "boolean"
        }
      ],
      owner_team: "storage",
      required_flags: [
        "enable_metadata_registry"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "normalizer",
        "cpu_runner"
      ],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Metadata Extract",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Value the non-fungibles a transaction moved from their collection floor. Opt-in: off unless enrichment.nft_prices is set.",
      failure_policy: "degrade",
      model_binding: null,
      name: "nft_price_enrich",
      needs: [
        "decode",
        "protocol_detect"
      ],
      options: [],
      owner_team: "prices",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Nft Price Enrich",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Normalize Starknet transactions, receipts and historical class evidence.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_starknet",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Normalize Starknet",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Read a Relay transfer's order id off the chain \u2014 the deposit's arguments, or the fill's raw calldata \u2014 and fall back to Relay's requests API only for the legs that carry none.",
      failure_policy: "degrade",
      model_binding: null,
      name: "relay_counterpart",
      needs: [
        "decode",
        "protocol_detect"
      ],
      options: [],
      owner_team: "protocols",
      required_flags: [
        "enable_relay_counterpart"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Relay Counterpart",
      version: "0.2.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Classify the normalized semantic action (CanonicalAction) for a tx.",
      failure_policy: "degrade",
      model_binding: null,
      name: "tx_action",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "protocols",
      required_flags: [
        "enable_tx_semantic"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "classifier"
      ],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Tx Action",
      version: "0.1.0"
    },
    {
      category: "general",
      code_runtime_id: null,
      custom: false,
      description: "Build render-ready transaction GraphScenes (semantic / flow) for the UI.",
      failure_policy: "degrade",
      model_binding: null,
      name: "tx_graph_build",
      needs: [
        "decode"
      ],
      options: [
        {
          default: null,
          description: "",
          enum_values: [],
          name: "kinds",
          required: false,
          type: "array"
        }
      ],
      owner_team: "graph",
      required_flags: [
        "tx_graph"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "builder",
        "cpu_runner"
      ],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Tx Graph Build",
      version: "0.1.0"
    },
    {
      category: "graph",
      code_runtime_id: null,
      custom: false,
      description: "Persist cluster nodes and entity \u2194 cluster relationships.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "cluster_assign",
      needs: [
        "graph_project"
      ],
      options: [
        {
          default: null,
          description: "",
          enum_values: [],
          name: "entity_enrichment_kinds",
          required: false,
          type: "array"
        },
        {
          default: 0,
          description: "",
          enum_values: [],
          name: "confidence_floor",
          required: false,
          type: "number"
        }
      ],
      owner_team: "graph",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Cluster Assign",
      version: "0.1.0"
    },
    {
      category: "graph",
      code_runtime_id: null,
      custom: false,
      description: "Resolve entities via catalog/graph evidence and merge into clusters.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "entity_resolve",
      needs: [
        "enrich_address_catalog"
      ],
      options: [
        {
          default: "entity",
          description: "",
          enum_values: [],
          name: "primary_entity_kind",
          required: false,
          type: "string"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "counterparty_enrichment_kinds",
          required: false,
          type: "array"
        }
      ],
      owner_team: "graph",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Entity Resolve",
      version: "0.1.0"
    },
    {
      category: "graph",
      code_runtime_id: null,
      custom: false,
      description: "Project transaction + resolved entities into the knowledge graph.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "graph_project",
      needs: [
        "entity_resolve"
      ],
      options: [
        {
          default: null,
          description: "",
          enum_values: [],
          name: "entity_enrichment_kinds",
          required: false,
          type: "array"
        },
        {
          default: true,
          description: "",
          enum_values: [],
          name: "emit_flow_edges",
          required: false,
          type: "boolean"
        }
      ],
      owner_team: "graph",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Graph Project",
      version: "0.1.0"
    },
    {
      category: "graph",
      code_runtime_id: null,
      custom: false,
      description: "Project the decoded semantic tree into the Neo4j/Neptune graph.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "semantic_graph_project",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "graph",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Semantic Graph Project",
      version: "0.3.0"
    },
    {
      category: "grouping",
      code_runtime_id: null,
      custom: false,
      description: "Drive the tx_grouping engine: detect groups, score, write overlays.",
      failure_policy: "degrade",
      model_binding: null,
      name: "group",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "storage",
      required_flags: [
        "enable_grouping"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "coordinator",
        "rules"
      ],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Group",
      version: "0.1.0"
    },
    {
      category: "grouping",
      code_runtime_id: null,
      custom: false,
      description: "Propagate CorrectionEvent rollback into the tx_grouping state machine.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "grouping_reorg_propagator",
      needs: [],
      options: [],
      owner_team: "storage",
      required_flags: [
        "enable_grouping"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "coordinator"
      ],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Grouping Reorg Propagator",
      version: "0.1.0"
    },
    {
      category: "metering",
      code_runtime_id: null,
      custom: false,
      description: "Gate the pipeline on the customer's plan quotas.",
      failure_policy: "fail_pipeline",
      model_binding: null,
      name: "quota_check",
      needs: [
        "normalize"
      ],
      options: [],
      owner_team: "billing",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Quota Check",
      version: "0.1.0"
    },
    {
      category: "ml",
      code_runtime_id: null,
      custom: false,
      description: "Next-step prediction over the journey so far (journey_next.v2, authe.sequence.v1).",
      failure_policy: "degrade",
      model_binding: {
        enrichment_kind: "ml_next_transaction",
        feature_schema: "journey_next.v2",
        name: "next-transaction"
      },
      name: "ml_next_transaction",
      needs: [
        "decode",
        "tx_action",
        "enrich_address_catalog",
        "group"
      ],
      options: [],
      owner_team: "ai",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Ml Next Transaction",
      version: "0.3.0"
    },
    {
      category: "ml",
      code_runtime_id: null,
      custom: false,
      description: "Score the transaction with a registered model (options.model_name): routing, prediction memo, shadows, human review and evidence gating.",
      failure_policy: "degrade",
      model_binding: {
        enrichment_kind: null,
        feature_schema: null,
        name: null
      },
      name: "ml_predict",
      needs: [
        "decode"
      ],
      options: [
        {
          default: null,
          description: "The registered model to run (its champion for this scope, with any candidates and shadows its routing names).",
          enum_values: [],
          name: "model_name",
          required: true,
          type: "string"
        },
        {
          default: null,
          description: "Kind the prediction is stored under. Must start with 'ml_'; defaults to ml_<model_name> with hyphens as underscores.",
          enum_values: [],
          name: "enrichment_kind",
          required: false,
          type: "string"
        },
        {
          default: null,
          description: "Feature extractor to use (e.g. tx_risk.v1). Defaults to the schema the registered model was trained on; a mismatch is refused.",
          enum_values: [],
          name: "feature_schema",
          required: false,
          type: "string"
        }
      ],
      owner_team: "ml",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Ml Predict",
      version: "0.1.0"
    },
    {
      category: "ml",
      code_runtime_id: null,
      custom: false,
      description: "Re-score risk with the platform risk model (tx_risk.v1): A/B, shadow, HITL.",
      failure_policy: "degrade",
      model_binding: {
        enrichment_kind: "ml_risk_score",
        feature_schema: "tx_risk.v1",
        name: "risk-rescorer"
      },
      name: "ml_risk_rescore",
      needs: [
        "risk_enrich",
        "enrich_address_catalog",
        "enrich_tx_catalog",
        "enrich_asset_metadata",
        "tx_action",
        "protocol_detect",
        "decode",
        "price_enrich"
      ],
      options: [],
      owner_team: "risk",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Ml Risk Rescore",
      version: "0.2.0"
    },
    {
      category: "ml",
      code_runtime_id: null,
      custom: false,
      description: "LLM-generated transaction summary (tx_summary_prompt.v1) with HITL.",
      failure_policy: "degrade",
      model_binding: {
        enrichment_kind: "ml_tx_summary",
        feature_schema: "tx_summary_prompt.v1",
        name: "tx-summarizer"
      },
      name: "ml_tx_summary",
      needs: [
        "decode",
        "tx_action",
        "protocol_detect",
        "enrich_address_catalog",
        "enrich_tx_catalog",
        "risk_enrich",
        "price_enrich"
      ],
      options: [],
      owner_team: "ai",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Ml Tx Summary",
      version: "0.2.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Simulate a pending transaction and attach its forecast effects.",
      failure_policy: "degrade",
      model_binding: null,
      name: "forecast",
      needs: [
        "mempool_ingress"
      ],
      options: [
        {
          default: null,
          description: "",
          enum_values: [],
          name: "simulator",
          required: false,
          type: "object"
        }
      ],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "mempool"
      ],
      title: "Forecast",
      version: "0.1.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize Aptos versioned transactions.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_aptos",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Normalize Aptos",
      version: "0.1.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize a Bitcoin/UTXO tx payload into the canonical UTXO model.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_btc",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Normalize Btc",
      version: "0.1.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize a Cosmos SDK LCD transaction (messages + ABCI events).",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_cosmos",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Normalize Cosmos",
      version: "0.1.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize participant-visible Corda, Fabric and Canton records.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_enterprise",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Normalize Enterprise",
      version: "0.1.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize a HyperCore (Hyperliquid L1) action into the canonical HyperCore model.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_hypercore",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Normalize Hypercore",
      version: "0.1.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize XRP Ledger and Tron explorer documents.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_ledger",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Normalize Ledger",
      version: "0.1.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize a pending mempool tx payload into the canonical model.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_mempool",
      needs: [
        "mempool_ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "mempool"
      ],
      title: "Normalize Mempool",
      version: "0.1.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize a Moralis tx payload into the canonical model.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_moralis",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Normalize Moralis",
      version: "0.1.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize NEAR asynchronous receipts.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_near",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Normalize Near",
      version: "0.2.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize a QuickNode JSON-RPC tx payload into the canonical model.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_quicknode",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Normalize Quicknode",
      version: "0.1.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize a Solana tx payload into the canonical Solana model.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_solana",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Normalize Solana",
      version: "0.1.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize Sui checkpointed transactions.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_sui",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Normalize Sui",
      version: "0.1.0"
    },
    {
      category: "normalize",
      code_runtime_id: null,
      custom: false,
      description: "Normalize TON asynchronous receipts.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "normalize_ton",
      needs: [
        "ingress"
      ],
      options: [],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Normalize Ton",
      version: "0.1.0"
    },
    {
      category: "persist",
      code_runtime_id: null,
      custom: false,
      description: "Phase 4 orchestrator: compact DDB header + S3 envelope body + discovery index fan-out (legacy single-row mode still supported).",
      failure_policy: "fail_step",
      model_binding: null,
      name: "persist",
      needs: [
        "decode"
      ],
      options: [
        {
          default: null,
          description: "",
          enum_values: [],
          name: "discovery_table_name",
          required: false,
          type: "string"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "dlq_queue_url",
          required: false,
          type: "string"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "table_name",
          required: false,
          type: "string"
        },
        {
          default: null,
          description: "",
          enum_values: [],
          name: "transaction_row_table_name",
          required: false,
          type: "string"
        }
      ],
      owner_team: "storage",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "db_helper",
        "dynamodb",
        "envelope_store",
        "public_envelope_store",
        "discovery_table",
        "sharding_policy",
        "tenant_recent_policy",
        "dlq",
        "dlq_offloader",
        "discovery_outbox",
        "cpu_runner"
      ],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool"
      ],
      title: "Persist",
      version: "2.0.0"
    },
    {
      category: "protocol",
      code_runtime_id: null,
      custom: false,
      description: "Emit DynamoDB index-row projections derived from ProtocolViews.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "index_project",
      needs: [
        "protocol_normalize"
      ],
      options: [],
      owner_team: "storage",
      required_flags: [
        "enable_index_projections"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "engine"
      ],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Index Project",
      version: "0.1.0"
    },
    {
      category: "protocol",
      code_runtime_id: null,
      custom: false,
      description: "Detect candidate protocols on a canonical transaction.",
      failure_policy: "degrade",
      model_binding: null,
      name: "protocol_detect",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "protocols",
      required_flags: [
        "enable_protocol_detection"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "detector"
      ],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Protocol Detect",
      version: "0.1.0"
    },
    {
      category: "protocol",
      code_runtime_id: null,
      custom: false,
      description: "Resolve and normalize detected protocols into ProtocolView enrichments.",
      failure_policy: "degrade",
      model_binding: null,
      name: "protocol_normalize",
      needs: [
        "decode"
      ],
      options: [],
      owner_team: "protocols",
      required_flags: [
        "enable_protocol_normalize"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "field_mapper",
        "resolver"
      ],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Protocol Normalize",
      version: "0.1.0"
    },
    {
      category: "protocol",
      code_runtime_id: null,
      custom: false,
      description: "Generate human-text + structured tx summaries per scope.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "summary",
      needs: [
        "protocol_normalize"
      ],
      options: [],
      owner_team: "protocols",
      required_flags: [
        "enable_summaries"
      ],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "generator"
      ],
      supported_tiers: [
        "confirmed",
        "finalized"
      ],
      title: "Summary",
      version: "0.1.0"
    },
    {
      category: "source",
      code_runtime_id: null,
      custom: false,
      description: "Pull raw transactions from a provider.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "ingress",
      needs: [],
      options: [
        {
          default: "alert",
          description: "",
          enum_values: [],
          name: "on_drift",
          required: false,
          type: "string"
        }
      ],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "adapter"
      ],
      supported_tiers: [
        "confirmed",
        "finalized",
        "mempool",
        "unconfirmed"
      ],
      title: "Ingress",
      version: "0.1.0"
    },
    {
      category: "source",
      code_runtime_id: null,
      custom: false,
      description: "Wrap a single streamed pending-tx payload as a ProcessingEnvelope.",
      failure_policy: "fail_step",
      model_binding: null,
      name: "mempool_ingress",
      needs: [],
      options: [
        {
          default: "alert",
          description: "",
          enum_values: [],
          name: "on_drift",
          required: false,
          type: "string"
        }
      ],
      owner_team: "ingestion",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "adapter"
      ],
      supported_tiers: [
        "mempool"
      ],
      title: "Mempool Ingress",
      version: "0.1.0"
    },
    {
      category: "triggers",
      code_runtime_id: null,
      custom: false,
      description: "Evaluate declarative trigger rules and dispatch actions.",
      failure_policy: "degrade",
      model_binding: null,
      name: "trigger_evaluate",
      needs: [
        "enrich_address_catalog"
      ],
      options: [],
      owner_team: "triggers",
      required_flags: [],
      retry_policy: {
        backoff_multiplier: 2,
        initial_backoff_seconds: 0.5,
        max_attempts: 1,
        max_backoff_seconds: 30
      },
      runtime_deps: [
        "evaluator",
        "dispatcher"
      ],
      supported_tiers: [
        "confirmed",
        "finalized",
        "unconfirmed"
      ],
      title: "Trigger Evaluate",
      version: "0.1.0"
    }
  ]
};

// src/pipeline/catalog.ts
var DEFAULT_STEP_CATALOG = catalogFromWire(
  step_catalog_generated_default
);
function indexCatalog(catalog) {
  return new Map(catalog.map((s) => [stepRefId(s), s]));
}
function findStep(catalog, name, version) {
  if (version) return catalog.find((s) => s.name === name && s.version === version);
  return catalog.find((s) => s.name === name);
}
function groupByCategory(catalog) {
  const groups = /* @__PURE__ */ new Map();
  for (const step of catalog) {
    const bucket = groups.get(step.category) ?? [];
    bucket.push(step);
    groups.set(step.category, bucket);
  }
  return [...groups.entries()].map(([category, steps]) => ({ category, steps }));
}

// src/pipeline/template-catalog.generated.json
var template_catalog_generated_default = {
  templates: [
    {
      category: "EVM",
      config: {},
      description: "Ingest \u2192 normalize \u2192 decode \u2192 protocol detect + normalize \u2192 metadata facts \u2192 enrich (price/risk/catalog) \u2192 graph \u2192 persist + index.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_instance_lifecycle: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "evm-standard",
      name: "EVM standard enrichment",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: null
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: null
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {},
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize",
            "price_enrich",
            "instance_lifecycle"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: null
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "instance_lifecycle",
          step_version: null
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "price_enrich",
          step_version: null
        },
        {
          needs: [
            "enrich_address_catalog",
            "price_enrich"
          ],
          node_name: null,
          options: {},
          step_name: "risk_enrich",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "enrich_address_catalog",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: null
        },
        {
          needs: [
            "price_enrich",
            "risk_enrich",
            "enrich_address_catalog",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "persist"
          ],
          node_name: null,
          options: {},
          step_name: "index_project",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "enrich_address_catalog",
            "protocol_detect",
            "context_signal_project",
            "price_enrich"
          ],
          node_name: null,
          options: {},
          step_name: "catalog_discover",
          step_version: "0.1.0"
        },
        {
          needs: [
            "instance_lifecycle",
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect",
            "risk_enrich",
            "price_enrich",
            "enrich_address_catalog"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    },
    {
      category: "Solana",
      config: {},
      description: "Solana family-dispatched pipeline: ingress \u2192 normalize (Solana) \u2192 decode \u2192 tx_action \u2192 graph \u2192 persist. The decode step routes by chain family to the decode_transaction.solana coordinator, which builds the SemanticTree from the parsed instructions + program registry + account model (no EVM ABI/proxy stack). tx_action then classifies the headline CanonicalAction (Jupiter swap / SPL / native transfer) from that tree, and tx_graph_build renders the base58 scene. The blueprint for adding any non-EVM chain via a ChainPipelineProfile.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_index_projections: true,
        enable_instance_lifecycle: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        enable_tx_semantic: true,
        tx_graph: true
      },
      id: "solana-standard",
      name: "Solana standard enrichment",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: null
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_solana",
          step_version: null
        },
        {
          needs: [
            "normalize_solana"
          ],
          node_name: null,
          options: {
            build_semantic: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_action",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "price_enrich",
          step_version: null
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "instance_lifecycle",
          step_version: null
        },
        {
          needs: [
            "protocol_normalize",
            "price_enrich",
            "instance_lifecycle"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: null
        },
        {
          needs: [
            "tx_action",
            "tx_graph_build",
            "price_enrich"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "persist"
          ],
          node_name: null,
          options: {},
          step_name: "index_project",
          step_version: null
        },
        {
          needs: [
            "instance_lifecycle",
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "tx_action",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    },
    {
      category: "Streaming",
      config: {},
      description: "Low-latency preliminary results from the mempool with minimal enrichment.",
      dynamic_step_budget: 4,
      feature_flags: {
        tx_graph: true
      },
      id: "mempool-fast-lane",
      name: "Mempool fast lane",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "mempool_ingress",
          step_version: null
        },
        {
          needs: [
            "mempool_ingress"
          ],
          node_name: null,
          options: {},
          step_name: "forecast",
          step_version: null
        },
        {
          needs: [
            "forecast"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_mempool",
          step_version: null
        },
        {
          needs: [
            "normalize_mempool"
          ],
          node_name: null,
          options: {},
          step_name: "risk_enrich",
          step_version: null
        },
        {
          needs: [
            "normalize_mempool"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: null
        },
        {
          needs: [
            "normalize_mempool",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        }
      ],
      samples: [],
      target_tier: "mempool"
    },
    {
      category: "Reorg",
      config: {},
      description: "Confirmed-tier pipeline with reorg detection and finality promotion.",
      dynamic_step_budget: 8,
      feature_flags: {
        tx_graph: true
      },
      id: "reorg-aware",
      name: "Reorg-aware confirmed",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: null
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: null
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {},
          step_name: "reorg_detector",
          step_version: null
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {},
          step_name: "finality_promote",
          step_version: null
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: null
        },
        {
          needs: [
            "finality_promote",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "finality_promote"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "Protocol pipeline for Chainlink CCIP ccipSend on Ethereum: ingress \u2192 normalize (Moralis) \u2192 decode \u2192 parse user-ops / Safe / proxy \u2192 build graph \u2192 persist. Mirrors the ccip_eth definition used by the local pipeline-run harness, with the render-ready cross-chain GraphScene baked in.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "chainlink-ccip-ethereum",
      name: "Chainlink CCIP (Ethereum)",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "context_proxy_write",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "reorg_detector",
          step_version: "0.1.0"
        },
        {
          needs: [
            "reorg_detector"
          ],
          node_name: null,
          options: {},
          step_name: "finality_promote",
          step_version: "0.1.0"
        },
        {
          needs: [
            "finality_promote",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {
        execution: {
          required_steps: [
            "group"
          ]
        }
      },
      description: "Cross-chain CCIP grouping: decode the Ethereum source ccipSend (CCIPMessageSent on the 1.6 OnRamp) and its Base destination execution (ExecutionStateChanged on the OffRamp) \u2014 both carrying the same CCIP messageId \u2014 then run the tx_grouping engine so the two legs converge into a single bridge.ccip group with a confidence score + evidence (even ingested out of order). Persists the decoded transactions to the application tables and the TransactionGroup state + tx\u2194group overlays to the grouping tables. Each leg is classified by tx_semantic (tx_action) and the completing leg reduces the group's headline action (group_action), which is persisted onto the group row so the group view can say what happened. Mirrors the ccip_eth_base_grouping pipeline-run definition with --grouping ddb_aws.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        enable_tx_semantic: true,
        tx_graph: true
      },
      id: "chainlink-ccip-cross-chain-grouping",
      name: "Chainlink CCIP cross-chain grouping",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            emit_metrics: false,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "context_proxy_write",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "reorg_detector",
          step_version: "0.1.0"
        },
        {
          needs: [
            "reorg_detector"
          ],
          node_name: null,
          options: {},
          step_name: "finality_promote",
          step_version: "0.1.0"
        },
        {
          needs: [
            "finality_promote",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_action",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "group_action",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: null,
          count: 2,
          description: "A single Chainlink CCIP message: the source ccipSend on Ethereum and its destination execution on Base. Grouping converges both legs into one bridge.ccip group.",
          id: "ccip-eth-base-message",
          name: "CCIP message: Ethereum \u2192 Base",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x1",
              label: "Ethereum source ccipSend",
              tx_hash: "0x0319d6769960690d637e0bac234272395e149ce35422169b85c3534f41a099bf"
            },
            {
              chain_id: "0x2105",
              label: "Base destination execution",
              tx_hash: "0x3858e79e42b937cc65355a6988acf80e3cbf0c6a13afe30b72f406e5f8cdcef3"
            }
          ]
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "Cross-chain Wormhole grouping across a multi-hop saga BSC \u2192 Ethereum \u2192 Base, carried by TWO guardian-signed VAAs. Each leg's composite VAA key (emitterChain + emitterAddress + sequence) is decoded \u2014 LogMessagePublished on the source Core Bridge, TransferRedeemed on the destination Token Bridge \u2014 then the tx_grouping engine converges the legs into bridge.wormhole groups with a confidence score + evidence (even ingested out of order). The Ethereum leg is a HUB: it redeems the inbound VAA from BSC AND publishes a new onward VAA to Base, so it is the DESTINATION of the first group and the SOURCE of the second \u2014 which is why WormholeScan lists multiple messages against that one transaction. Persists the decoded transactions to the application tables and the TransactionGroup state + tx\u2194group overlays to the grouping tables. Definition-driven (the wormhole ProtocolDefinition owns the composite cross-chain linkage).",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "wormhole-cross-chain-grouping",
      name: "Wormhole cross-chain grouping",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            emit_metrics: false,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: null,
          count: 3,
          description: "A multi-hop Wormhole saga spanning two Portal Token Bridge transfers / two VAAs. The BSC source transfer publishes VAA #1; the Ethereum leg is a HUB that redeems VAA #1 AND re-bridges onward, publishing VAA #2 to Base; the Base leg redeems VAA #2 (the 'Executed Tx Hash' on WormholeScan) and delivers the actual amount. Grouping converges the legs into TWO bridge.wormhole groups that share the Ethereum hub (destination of the first, source of the second), each on its own composite VAA key.",
          id: "wormhole-bsc-eth-base-multihop",
          name: "Wormhole multi-hop: BSC \u2192 Ethereum \u2192 Base",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x38",
              label: "BSC source Token Bridge transfer (VAA #1)",
              tx_hash: "0x068900a166004c67cd05da8d2fdd94d6afec48955baa5dcd359d3034c45c0770"
            },
            {
              chain_id: "0x1",
              label: "Ethereum hub: redeem VAA #1 + publish VAA #2",
              tx_hash: "0x56ce6bb2bfcc76a7a8592bf0196e7f8edea7298232aa6bc9060882686783e984"
            },
            {
              chain_id: "0x2105",
              label: "Base destination completeTransfer (VAA #2)",
              tx_hash: "0xe03e3763adb657b231567cd72415b67f5d0416de9a2545f58a4049517e47ea56"
            }
          ]
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "An intent's two halves, grouped. A CoW order is PLACED in one transaction and FILLED in another \u2014 same chain, minutes apart, and by different senders: the trader places, a solver fills. The two touch different contracts and move different tokens, so nothing about them looks related. What ties them is the orderUid, the protocol's own identity for the order (EIP-712 digest + owner + validTo). The settlement's Trade log states it verbatim; the ETH-flow placement does NOT, so the decoder derives it from the order the OrderPlacement log states and stamps it under the same fact \u2014 which is what lets the tx_grouping engine converge the two legs into one exchange.cowswap_order group. Deliberately NOT matched on appData: that is a hash of interface metadata (app code, slippage, version) with no order-specific content, so thousands of unrelated orders share one value \u2014 it agrees on any single pair and groups strangers at scale. Only ETH-flow and pre-signed orders have an on-chain placement; an ordinary CoW order is signed off-chain, so its fill correctly stands alone and the saga completes on the FILL role alone. Definition-driven (the cowswap ProtocolDefinition owns the order-uid linkage + the CoW Explorer deep-link).",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "cowswap-order-lifecycle-grouping",
      name: "CoW Protocol order lifecycle grouping",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            emit_metrics: false,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: null,
          count: 2,
          description: "A native-ETH (ETH-flow) order on Ethereum. The placement escrows 0.01 ETH through the ETH-flow wrapper and emits OrderPlacement; a solver's settlement fills it minutes later and the GPv2Settlement emits Trade. Both legs carry order uid 0xd8173b42\u2026ffffffff \u2014 verbatim on the fill, derived on the placement \u2014 so they converge into ONE completed exchange.cowswap_order group. Two traps this sample exercises: the validTo inside the signed order is the ETH-flow sentinel 0xffffffff and not the user's deadline, and the order's owner is the ETH-flow CONTRACT rather than the person who sent the transaction.",
          id: "cowswap-ethflow-place-then-fill",
          name: "CoW ETH-flow: place, then fill",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x1",
              label: "Ethereum PLACEMENT (ETH-flow createOrder)",
              tx_hash: "0x08615d36ac50055ab6e2aa32ccaf93dd70c903589c8ec8958faa44b92d052c3e"
            },
            {
              chain_id: "0x1",
              label: "Ethereum FILL (solver settlement, Trade log)",
              tx_hash: "0xcda47977f16dc49e8f2065cca203b1cac2fc7d1ae9dc221a39ea0b6aeb34154a"
            }
          ]
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "One prediction market's whole story, grouped on the question it asks. Six transactions spread over sixty thousand blocks \u2014 the market is created, listed for trading, positions are opened and closed on it, somebody trades shares on the order book, and finally it resolves \u2014 with different senders, different contracts and different tokens at every step. What ties them is the conditionId: the oracle, the question and the outcome count hashed together, which every position on the market shares. It is in a DIFFERENT PLACE on every leg, which is the whole reason this is a spec rather than a rule. ConditionPreparation and ConditionResolution key on the market, so it is topics[1]. PositionSplit and PositionsMerge key on the stakeholder and carry the market third. PayoutRedemption does not index it at all \u2014 it indexes the collateral token instead \u2014 so the market sits inline in the data. The order book cannot state it in any of those places: a fill names the ERC-1155 outcome share and never the question, and the derivation between them runs one way only (positionId is a keccak over the collateral and a collectionId, itself an alt_bn128 curve point over the conditionId and an index set). So the market is STAMPED on the fill, read back from the exchange's own TokenRegistered listing through the context store \u2014 which makes this template a test of the cross-transaction store as much as of the grouping engine. Feed the listing before the trade or the trade correctly joins nothing. Definition-driven: `conditional_tokens` owns the CTF's three lifecycle events, `polymarket_ctf_exchange` owns the order book's two generations, and `commons.grouping.markets` owns the spec.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "polymarket-market-lifecycle-grouping",
      name: "Polymarket market lifecycle grouping",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            emit_metrics: false,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize",
            "context_market_write"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "context_market_write",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "context_market_write"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group",
            "context_market_write"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: null,
          count: 6,
          description: "Market 0xf2b1250d\u2026910bd on Polygon, in chain order. Prepared at block 91,942,581 and listed for trading forty-nine blocks later; split, traded and merged around block 91,999,xxx; resolved at 92,000,172. All six legs converge into ONE open market.lifecycle group. The trade is the leg that matters. It is a PURE SECONDARY trade \u2014 both sides already held shares, so the exchange only moved them and no CTF lifecycle event fired at all. Measured over 80 real settlements, 67 already grouped because they split or merged in the same transaction; 13 looked exactly like this one and joined nothing. It groups here only because the listing three legs earlier taught the context store which market that share belongs to. The group stays OPEN after resolution on purpose: holders redeem for months afterwards, so resolution is a leg and not an ending.",
          id: "polymarket-one-market-end-to-end",
          name: "One Polygon market, created to resolved",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x89",
              label: "Polygon market CREATED (ConditionPreparation)",
              tx_hash: "0x56767567db978d9365a65a3d6ad0564872228927238e23e57f53ae46631870bf"
            },
            {
              chain_id: "0x89",
              label: "Polygon market LISTED (TokenRegistered \u2014 the index)",
              tx_hash: "0xc2b85d10c1b1bc749c7ea085e70f948990e7fb55792ac33d3ebcdaf05da3a064"
            },
            {
              chain_id: "0x89",
              label: "Polygon position OPENED (PositionSplit)",
              tx_hash: "0x87bc3abe8435e7552b7bf81aa3259892145e2a7f90ad15a9bfa2a4ec788bfd2a"
            },
            {
              chain_id: "0x89",
              label: "Polygon SECONDARY TRADE (OrderFilled \u2014 stamped)",
              tx_hash: "0x14823b37c047e8467e6bef00f30567c7e585f282043a8391431aaba90e2a1af2"
            },
            {
              chain_id: "0x89",
              label: "Polygon position CLOSED (PositionsMerge)",
              tx_hash: "0x3411d6dc613dfa66afb6d0263b503ede5f74a549678ef662b2d032d7e07451aa"
            },
            {
              chain_id: "0x89",
              label: "Polygon market RESOLVED (ConditionResolution)",
              tx_hash: "0x869cdf4a193b6f11caecbf0af81de94371c17cdda9fe7f86860d7a339a89ac49"
            }
          ]
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "System pipeline for Polymarket on Polygon: decode every order fill and market resolution, remember which market each outcome share belongs to, file each fill (wallet, market, outcome, side, shares, dollars, price) and each resolution as warehouse facts, and when a market resolves, settle every wallet that traded it \u2014 won or lost, P&L, and whether a cheap winning position was bought shortly before the resolution (an informed entry, alertable as a situation). Run with discovery_mode=event_filter; the template supplies its discovery topics.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "polymarket-polygon",
      name: "Polymarket on Polygon (trades, resolutions, settlement)",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            emit_metrics: false,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {
            fill_facts: false
          },
          step_name: "context_market_write",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize",
            "context_market_write"
          ],
          node_name: null,
          options: {
            fact_namespaces: [
              "enriched.prediction"
            ],
            write_artifacts: false
          },
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "reorg_detector",
          step_version: "0.1.0"
        },
        {
          needs: [
            "reorg_detector"
          ],
          node_name: null,
          options: {},
          step_name: "finality_promote",
          step_version: "0.1.0"
        },
        {
          needs: [
            "finality_promote",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy",
            "context_market_write"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group",
            "context_market_write"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "Cross-chain Mayan grouping across the Mayan Forwarder's sub-protocols (Swift / Swift v2 / MCTP / mono-chain swap). The Forwarder entrypoint is unverified-rotation-resistant (claimed by selector, ABI resolved at runtime) and most real transactions are aggregator-wrapped (MetaMask MetaBridge, ERC-4337, LiFi) so the Mayan call lands in an INTERNAL transaction \u2014 the decode step walks internal txs, resolves the verified Mayan/DEX ABIs, and peels the opaque mayanData blob from the definition-owned layouts (version + block aware). For a Mayan Swift swap the source SEND, destination FULFILL, and source UNLOCK legs all carry the SAME Mayan order hash, so the tx_grouping engine converges them into one bridge.mayan group with a confidence score + evidence (even ingested out of order). MCTP (Circle CCTP + Wormhole) and a same-chain mono-chain swap decode as standalone intents. Persists the decoded transactions to the application tables and the TransactionGroup state + tx\u2194group overlays to the grouping tables. Definition-driven (the mayan ProtocolDefinition owns the order-hash cross-chain linkage + the Mayan Explorer deep-link).",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "mayan-cross-chain-grouping",
      name: "Mayan cross-chain grouping",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            emit_metrics: false,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: null,
          count: 3,
          description: "A Mayan Swift v2 order (BSC \u2192 Ethereum). The source SEND locks funds on BSC (wrapped via MetaMask MetaBridge \u2192 LiFi \u2192 the Mayan Forwarder), the destination FULFILL delivers them to the user on Ethereum, and the source UNLOCK releases the locked funds to the solver on BSC after proving fulfillment. All three legs carry the same Mayan order hash, so grouping converges them into one bridge.mayan group (SEND + FULFILL as source/destination, UNLOCK as the source-side settlement).",
          id: "mayan-swift-v2-bsc-eth",
          name: "Mayan Swift v2: BSC \u2192 Ethereum",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x38",
              label: "BSC source SEND (Swift order created)",
              tx_hash: "0xd0a6be5d23f6c9752d0ff842fb953f4456a1c0d2895be71ffd56265f127b8739"
            },
            {
              chain_id: "0x1",
              label: "Ethereum destination FULFILL",
              tx_hash: "0x7d0b5362274891f90a691aa09a69124fce0ab04169cea12822c0663eace5277d"
            },
            {
              chain_id: "0x38",
              label: "BSC source UNLOCK (solver settles)",
              tx_hash: "0x4c9113ad5ff2f0ba3f6bf165d2e5a2154a4ade8ce4fbd9324c8e45c174c5a277"
            }
          ]
        },
        {
          address: null,
          chain_id: null,
          count: 2,
          description: "A Mayan Swift order between Ethereum and Solana \u2014 only the EVM legs are decoded here (the Solana SETTLE / FULFILL / REGISTER_ORDER legs are out of EVM decode scope). The Ethereum SEND creates the order and the Ethereum UNLOCK settles it; both carry the same Mayan order hash and group into one bridge.mayan saga whose counterpart leg lives on Solana.",
          id: "mayan-swift-eth-solana-evm-legs",
          name: "Mayan Swift: Ethereum \u2194 Solana (EVM legs)",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x1",
              label: "Ethereum SEND (Swift order created)",
              tx_hash: "0x2013b31a0ac6f73fd492dbd986f000253aa284fabe752ba3b6a03d485e6182fe"
            },
            {
              chain_id: "0x1",
              label: "Ethereum UNLOCK (order settled)",
              tx_hash: "0x568289c909bb7193843faf1d52ade55ba05625c2c37f26294ddec8156b5d3eb8"
            }
          ]
        },
        {
          address: null,
          chain_id: null,
          count: 2,
          description: "A Mayan MCTP (Mayan Circle Transfer Protocol) USDC transfer Ethereum \u2192 Base. The source SEND (aggregator-wrapped ERC-4337 \u2192 LiFi \u2192 the Mayan Forwarder \u2192 MCTP bridgeWithFee) burns USDC through Circle CCTP (depositForBurnWithCaller) and publishes the order as a Wormhole message (LogMessagePublished); the destination SETTLE (redeemWithFee) verifies that VAA, mints USDC via CCTP, pays the relayer fee, and delivers the remainder to the recipient. Both legs reference the SAME Wormhole VAA (emitterAddress + sequence), so grouping converges them into one bridge.mayan group (SEND source / SETTLE destination) with a Mayan Explorer deep-link.",
          id: "mayan-mctp-eth-base",
          name: "Mayan MCTP: Ethereum \u2192 Base",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x1",
              label: "Ethereum source SEND (MCTP bridgeWithFee)",
              tx_hash: "0x76fb83df14db62c00b6ed457626987a3cb7172f6e55cc1a88430bc63a01f0571"
            },
            {
              chain_id: "0x2105",
              label: "Base destination SETTLE (MCTP redeemWithFee)",
              tx_hash: "0xbbe36d09019c40a1acf774a1244f6d160f28d06f9efba2bd9364eda9c9483f97"
            }
          ]
        },
        {
          address: null,
          chain_id: null,
          count: 1,
          description: "A Mayan same-chain (mono-chain) swap on Ethereum through the Forwarder + a Uniswap V4 route. No cross-chain group \u2014 it decodes as a single swap intent with a Mayan Explorer deep-link.",
          id: "mayan-mono-chain-eth",
          name: "Mayan mono-chain swap (Ethereum)",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x1",
              label: "Ethereum mono-chain swap",
              tx_hash: "0xc283e5a7f3fccf89c79e9b0c0f0bb145493c431348ba11eecc600895552518d8"
            }
          ]
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "Tokens",
      config: {},
      description: "Resolve token-definition metadata (symbol / decimals / name / logo) next to every token address a transaction touches. ingress \u2192 normalize (Moralis) \u2192 decode (calldata + ERC-20/721 events + proxy resolution, build_semantic on) \u2192 metadata_extract \u2192 tx_graph_build \u2192 persist. The decode step hands each token address to the token SSOT (commons.tokens: bundled registry \u2192 our own DynamoDB cache \u2192 third-party Moralis, with negative caching + self-hosted logos), so each SemanticAsset carries its symbol + decimals beside its token_address and the GraphScene assets carry their logo. Run it through the local pipeline-run harness with the token_definitions definition (decode mode: abi) to resolve unknown tokens live from the configured third parties.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "token-definitions",
      name: "Token definitions enrichment",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "enrich_asset_metadata",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: null,
          count: 2,
          description: "Two real EVM transactions that move ERC-20 tokens \u2014 one on Ethereum, one on Base. Each is decoded and every token address is resolved to its symbol / decimals / name / logo via the token SSOT (bundled registry \u2192 our DynamoDB cache \u2192 third-party Moralis), so the metadata renders next to the addresses. Mirrors the token_definitions pipeline-run harness fixtures.",
          id: "token-defs-evm-multichain",
          name: "Token metadata: Ethereum + Base",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x1",
              label: "Ethereum tx \u2014 resolve token defs next to transfer addresses",
              tx_hash: "0x0319d6769960690d637e0bac234272395e149ce35422169b85c3534f41a099bf"
            },
            {
              chain_id: "0x2105",
              label: "Base tx \u2014 resolve token defs next to transfer addresses",
              tx_hash: "0x3858e79e42b937cc65355a6988acf80e3cbf0c6a13afe30b72f406e5f8cdcef3"
            }
          ]
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "Protocol pipeline for the whole Uniswap family on Ethereum: ingress \u2192 normalize (Moralis) \u2192 decode \u2192 parse user-ops / Safe / proxy \u2192 build graph \u2192 persist. Decodes V1 factory/exchange swaps + liquidity, V2/V3 router swaps, V4 PositionManager NFT liquidity, and the Universal Router command dispatch (with Permit2 pulls + wrap/unwrap), peeling the nested calldata each packs behind routers and aggregators. build_semantic on so the swap/liquidity tree lands in the render-ready GraphScene. Ships one runnable sample per version family (V1 / V2 / V3 / V4 position / Permit2).",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_instance_lifecycle: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "uniswap-family",
      name: "Uniswap family (V1-V4 + Permit2)",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "price_enrich",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize",
            "price_enrich",
            "instance_lifecycle"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "instance_lifecycle",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build",
            "price_enrich"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "instance_lifecycle",
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect",
            "price_enrich"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: "0x1",
          count: 2,
          description: "Two Uniswap V3 swaps on Ethereum \u2014 exact-in / exact-out single- and multi-hop routes through the SwapRouter (exactInput* path encoding + pool Swap events). Decodes the V3 path segments, fee tiers, and in/out amounts.",
          id: "uniswap-v3-swaps",
          name: "Uniswap V3 swaps (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0x87de1f38787d10058e82efdcb4851bd769bc48e1eeb40c4b7f63bfbd72e47fc8",
            "0xfe5efc11fa1576a5737f51155789c0f8a62c6f20b263df42fbe3bcef0b359e52"
          ],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 4,
          description: "Four Uniswap V4 PositionManager transactions on Ethereum \u2014 mint / modify / collect / burn of the ERC-721 liquidity position (the modifyLiquidities command batch + PoolManager settlement events). Exercises the V4 position + currency pair decode.",
          id: "uniswap-v4-position",
          name: "Uniswap V4 Position NFT (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0x9be1ba8a2ba8963bed8f9fce1e321848424fa2503ede055836ed9730d49b4721",
            "0xd7ed13b72c7a1cc5782d1041a256c49b787a92d986c472dfaf4894fe38880558",
            "0x112984a0abf5f60574580a0d8f13768afc8827fc50d91fb884803fd92d760e80",
            "0x86f5f6659e48c30388bc71b701682013be690dd051cad57edeed153c0d24428d"
          ],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 3,
          description: "Three Uniswap V1 transactions on Ethereum \u2014 the original 2018 Vyper AMM's per-token exchange swap / liquidity verbs (ethToTokenSwapInput / tokenToEthSwapInput / tokenToTokenSwapInput), claimed by the V1 exchange plugin's distinctive selectors.",
          id: "uniswap-v1-swaps",
          name: "Uniswap V1 swaps (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0x1dd2f4fa5e77159d20e2f7b8882f21b9f24b3fee71336e1249788885cf319f61",
            "0xe5e35ee13bb6326df4da89f17504a81923299d4986de06a019ca7856cbe76bca",
            "0x4824def63ba420570e59acf10f32bd935d7a0a0d67e6eb07e4c0ae4e270b71d1"
          ],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 1,
          description: "A Uniswap V2 router swap on Ethereum (swapExactTokensForTokens family) \u2014 the constant-product pair path + Swap events.",
          id: "uniswap-v2-swap",
          name: "Uniswap V2 swap (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0x006a041ade99b4715e6578ac968932f47fa9f1be1a676ad66678260ca0fbb6d1"
          ],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 1,
          description: "A Permit2 transaction on Ethereum \u2014 the approval rung of the Uniswap stack (permit / approve / transferFrom), decoded via the Permit2 emitter so the spender / token / amount surface.",
          id: "uniswap-permit2",
          name: "Permit2 approval (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0x36ccf5bfee79c946dd4ba4e0cc59d2489acf703988fd709311323cf124a23703"
          ],
          tx_specs: []
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "MEV",
      config: {},
      description: "Protocol pipeline for the Banana Gun Router V2 on Ethereum: ingress \u2192 normalize (Moralis) \u2192 decode \u2192 parse user-ops / Safe / proxy \u2192 build graph \u2192 persist. Decodes the bot's buy (ETH \u2192 token) / sell (token \u2192 ETH/WETH) entrypoints \u2014 anchoring side, downstream DEX, token pair, amounts + platform fee as highlights \u2014 and forwards the actual swap to the downstream DEX (Uniswap V2/V3 + forks) via internal txs + Transfer/Swap events. build_semantic on so the SWAP tree lands in the render-ready GraphScene. Ships a 'latest N router txs' sample and a hardcoded buy/sell set.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "banana-gun-router",
      name: "Banana Gun router (MEV trading bot)",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: "0x3328f7f4a1d1c57c35df56bbf0c9dcafca309c49",
          chain_id: "0x1",
          count: 5,
          description: "The latest 5 transactions hitting the Banana Gun Router V2 on Ethereum \u2014 discovered live via the explorer provider (no selector filter, so both buys and sells are pulled). The 'pick the latest N' path: each is fetched verbose (full logs + internal txs) and run through the MEV decode.",
          id: "banana-gun-latest",
          name: "Banana Gun latest (last 5 router txs)",
          selector: null,
          source_kind: "latest",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 3,
          description: "Three verified Banana Gun Router V2 transactions on Ethereum \u2014 two buys (ETH \u2192 token via Uniswap V2) and one sell (token \u2192 WETH via Uniswap V2, fully anchored: token pair / pool / amountIn / amountOutMin / recipient). The reproducible, no-network set.",
          id: "banana-gun-buys-sells",
          name: "Banana Gun buys + sell (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0x9933e82d1b4f4fc5b4bcaec0c60c79b0a1a57745799546b13a2fc3260421ac7a",
            "0xf1d7181e3521a1e46e96626a21253b328cbde720a03443dec82e37669bff29d1",
            "0x220541c9a57d4b58391b0c46647e8d2ffe8667bfae30b2f24e989a4396c0c354"
          ],
          tx_specs: []
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "MEV",
      config: {},
      description: "Protocol pipeline for the generic MEV bot (0x98c3\u20266b8e) on Ethereum: ingress \u2192 normalize (Moralis) \u2192 decode \u2192 parse user-ops / Safe / proxy \u2192 build graph \u2192 persist. A closed-source arbitrage/sandwich searcher that calls a Uniswap V2 pair / V3 pool DIRECTLY with hand-packed calldata \u2014 the decoder pins the venue from the selector and anchors amountIn / amountOutMin / pair / tokenOut (V2) or pool / amountSpecified (V3), marks it IS_ARB for the single-tx net-profit headline, and delegates the swap math to the pool's own decode + the Transfer flow. Ships a 'latest N' sample and a hardcoded V2+V3 set.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "mev-bot-generic",
      name: "Generic MEV bot (direct-pool searcher)",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: "0x98c3d3183c4b8a650614ad179a1a98be0a8d6b8e",
          chain_id: "0x1",
          count: 5,
          description: "The latest 5 transactions hitting the generic MEV bot (0x98c3\u20266b8e) on Ethereum \u2014 discovered live via the explorer provider (no selector filter). Each is fetched verbose (full logs + internal txs) and run through the MEV decode.",
          id: "mev-bot-latest",
          name: "Generic MEV bot latest (last 5 txs)",
          selector: null,
          source_kind: "latest",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 2,
          description: "Two verified generic-MEV-bot txs on Ethereum: a direct Uniswap V2 pair swap (0xce2e62ff \u2014 anchors amountIn / amountOutMin / pair / tokenOut) and a direct Uniswap V3 pool swap (0x49c36c07 \u2014 anchors pool / amountSpecified / zeroForOne). The reproducible, no-network set.",
          id: "mev-bot-v2-v3",
          name: "Generic MEV bot V2 + V3 (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0xebec2abc8affbaa27bc76839237e1d1de151d51e7ab340614f422b9f4cff4083",
            "0xfc1359a6cc9369c78d740e79fc1b60fe0ccbaa1d04963a677b6d8c5905bcc428"
          ],
          tx_specs: []
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "MEV",
      config: {},
      description: "Protocol pipeline for jaredfromsubway.eth (0x1f2f\u2026f387) on Ethereum: ingress \u2192 normalize (Moralis) \u2192 decode \u2192 parse user-ops / Safe / proxy \u2192 build graph \u2192 persist. A heavily obfuscated sandwich/arb searcher that shares the generic bot's direct-pool dialect (reused layouts, IS_ARB net-profit headline). Its live entrypoints mostly use selectors the plugin does not yet model, so those decode through the generic seams (ERC-20 Transfer flows show the sandwich legs); the plugin claims jared only on the shared direct-pool selectors. Ships a 'latest N' sample and a hardcoded set of recent txs.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "jaredfromsubway",
      name: "jaredfromsubway.eth (MEV sandwich bot)",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: "0x1f2f10d1c40777ae1da742455c65828ff36df387",
          chain_id: "0x1",
          count: 5,
          description: "The latest 5 transactions hitting jaredfromsubway.eth (0x1f2f\u2026f387) on Ethereum \u2014 discovered live via the explorer provider. Each is fetched verbose (full logs + internal txs); the sandwich legs surface as ERC-20 Transfer flows.",
          id: "jaredfromsubway-latest",
          name: "jaredfromsubway latest (last 5 txs)",
          selector: null,
          source_kind: "latest",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 2,
          description: "Two verified recent jaredfromsubway.eth txs on Ethereum. Their obfuscated entrypoints currently decode through the generic seams \u2014 the ERC-20 Transfer flows expose the multi-hop sandwich (WETH \u2194 token movements) \u2014 rather than the plugin's anchored swap headline. The reproducible, no-network set.",
          id: "jaredfromsubway-recent",
          name: "jaredfromsubway recent txs (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0xfc71603c2539bd4293c669ade4ce61f5ffa93750c4f744e2f4e9767640f8cacc",
            "0x3041400be313a5c7a268376875ed08af10162f775cf22b293e05c2c3a9996a2d"
          ],
          tx_specs: []
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "MEV",
      config: {},
      description: "Protocol pipeline for the Wintermute market-maker router (0x51c7\u20262a7f) on Ethereum: ingress \u2192 normalize (Moralis) \u2192 decode \u2192 parse user-ops / Safe / proxy \u2192 build graph \u2192 persist. A closed-source MM router speaking several downstream dialects \u2014 Uniswap V2/V3 buy+sell, Curve tricrypto, Balancer, a V3 LP mint, a batch transfer \u2014 each pinned by its own selector; the decoder anchors the side / venue / pool / token / amounts per dialect and emits a typed buy/sell (or mint/transfer) headline, delegating the settled movements to the Transfer flow. Ships a 'latest N' sample and a hardcoded buy+sell set.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "wintermute",
      name: "Wintermute market maker",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: "0x51c72848c68a965f66fa7a88855f9f7784502a7f",
          chain_id: "0x1",
          count: 5,
          description: "The latest 5 transactions hitting the Wintermute market-maker router (0x51c7\u20262a7f) on Ethereum \u2014 discovered live via the explorer provider (no selector filter, so buys, sells and the other dialects are pulled). Each is fetched verbose and run through the MEV decode.",
          id: "wintermute-latest",
          name: "Wintermute latest (last 5 txs)",
          selector: null,
          source_kind: "latest",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 2,
          description: "Two verified Wintermute txs on Ethereum: a Uniswap V3 pool sell (0x771d503f \u2014 anchors pool / amountSpecified / zeroForOne) and a Uniswap V3 pool buy (0x122067ed \u2014 anchors pool / amount0Delta / amount1Delta). The reproducible, no-network set.",
          id: "wintermute-buy-sell",
          name: "Wintermute buy + sell (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0xd1d471339cfada7b11468c91c7350fb24c679e17e514d03bcedcbec088efc76e",
            "0x0c61fc3e8a5ca0dfae83aaf646eb1a91d17cfc9eb3e59dd9b0bb1b88066fe5d1"
          ],
          tx_specs: []
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "EVM",
      config: {
        batch: {
          prefetch: {
            max_prefetch_addresses: 1e3
          }
        }
      },
      description: "Chain-wide EVM pipeline: ingest every transaction in the block window, decode it against the whole bundled protocol catalogue (bridges, aggregators, exchanges, MEV bots, oracles, Safe) and record plain value transfers as native_transfer. Pair with discovery_mode=full_block on the system pipeline row \u2014 an event filter cannot see native transfers or selector-claimed protocols.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        enable_tx_semantic: true,
        tx_graph: true
      },
      id: "evm-all-protocols",
      name: "EVM all protocols + native transfers",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_quicknode",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_quicknode"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "enrich_address_catalog",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "enrich_asset_metadata",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize",
            "enrich_address_catalog"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.3.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "context_proxy_write",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "reorg_detector",
          step_version: "0.1.0"
        },
        {
          needs: [
            "reorg_detector"
          ],
          node_name: null,
          options: {},
          step_name: "finality_promote",
          step_version: "0.1.0"
        },
        {
          needs: [
            "finality_promote",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "persist"
          ],
          node_name: null,
          options: {},
          step_name: "index_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_action",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode",
            "enrich_address_catalog"
          ],
          node_name: null,
          options: {},
          step_name: "context_flow_write",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "context_dust_write",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode",
            "enrich_asset_metadata",
            "enrich_address_catalog"
          ],
          node_name: null,
          options: {},
          step_name: "context_poisoning_write",
          step_version: "0.1.0"
        },
        {
          needs: [
            "enrich_address_catalog",
            "protocol_detect",
            "context_signal_project",
            "enrich_asset_metadata"
          ],
          node_name: null,
          options: {},
          step_name: "catalog_discover",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group",
            "context_flow_write",
            "context_dust_write",
            "context_poisoning_write"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect",
            "enrich_address_catalog",
            "catalog_discover",
            "enrich_asset_metadata"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    },
    {
      category: "Private ledgers",
      config: {
        enterprise_reconciliation: {
          claim_bindings: {},
          sources: {}
        }
      },
      description: "Ingest authorized participant exports, decode private application mappings, reconcile signed cash/custody evidence, group lifecycles and persist private results. Configure mappings and feed public keys for your deployment.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_tx_semantic: true,
        tx_graph: true
      },
      id: "private-corda",
      name: "Private Corda 5 ledger",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: null
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_enterprise",
          step_version: null
        },
        {
          needs: [
            "normalize_enterprise"
          ],
          node_name: null,
          options: {},
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "reconcile_enterprise",
          step_version: null
        },
        {
          needs: [
            "decode",
            "reconcile_enterprise"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_action",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: null
        },
        {
          needs: [
            "group",
            "reconcile_enterprise",
            "tx_action",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "reconcile_enterprise",
            "tx_action"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    },
    {
      category: "Private ledgers",
      config: {
        enterprise_reconciliation: {
          claim_bindings: {},
          sources: {}
        }
      },
      description: "Ingest authorized participant exports, decode private application mappings, reconcile signed cash/custody evidence, group lifecycles and persist private results. Configure mappings and feed public keys for your deployment.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_tx_semantic: true,
        tx_graph: true
      },
      id: "private-fabric",
      name: "Private Fabric 2.5 ledger",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: null
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_enterprise",
          step_version: null
        },
        {
          needs: [
            "normalize_enterprise"
          ],
          node_name: null,
          options: {},
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "reconcile_enterprise",
          step_version: null
        },
        {
          needs: [
            "decode",
            "reconcile_enterprise"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_action",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: null
        },
        {
          needs: [
            "group",
            "reconcile_enterprise",
            "tx_action",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "reconcile_enterprise",
            "tx_action"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    },
    {
      category: "Private ledgers",
      config: {
        enterprise_reconciliation: {
          claim_bindings: {},
          sources: {}
        }
      },
      description: "Ingest authorized participant exports, decode private application mappings, reconcile signed cash/custody evidence, group lifecycles and persist private results. Configure mappings and feed public keys for your deployment.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_tx_semantic: true,
        tx_graph: true
      },
      id: "private-canton",
      name: "Private Canton ledger",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: null
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_enterprise",
          step_version: null
        },
        {
          needs: [
            "normalize_enterprise"
          ],
          node_name: null,
          options: {},
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "reconcile_enterprise",
          step_version: null
        },
        {
          needs: [
            "decode",
            "reconcile_enterprise"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_action",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: null
        },
        {
          needs: [
            "group",
            "reconcile_enterprise",
            "tx_action",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "reconcile_enterprise",
            "tx_action"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    }
  ]
};

// src/pipeline/templates.ts
function findTemplate(id) {
  if (!id) return void 0;
  return DEFAULT_TEMPLATES.find((t) => t.id === id);
}
function templateSamplesFor(templateId) {
  return findTemplate(templateId)?.samples ?? [];
}
function stackedPosition(index) {
  return { x: 120, y: 80 + index * 120 };
}
function instantiateTemplate(template, overrides = {}) {
  const doc = createPipelineDoc({
    name: overrides.name ?? template.name,
    description: overrides.description ?? template.description,
    targetTier: overrides.targetTier ?? template.targetTier ?? LatencyTier.CONFIRMED,
    dynamicStepBudget: overrides.dynamicStepBudget ?? template.dynamicStepBudget ?? 8,
    featureFlags: overrides.featureFlags ?? template.featureFlags,
    // Deep-cloned: the gallery is a module-level singleton, so aliasing it here
    // would let an edit to one instantiated doc rewrite the template itself.
    configOverlay: overrides.configOverlay ?? (template.config ? structuredClone(template.config) : void 0),
    // Stamp the originating template so the run console can offer this
    // template's run samples (carried as `metadata.template_id` on the wire).
    templateId: overrides.templateId ?? template.id,
    ...overrides,
    nodes: []
  });
  const nodes = [];
  const renamed = /* @__PURE__ */ new Map();
  template.nodes.forEach((tn, i) => {
    const declaredName = tn.nodeName ?? tn.stepName;
    const nodeName = uniqueNodeName({ ...doc, nodes }, declaredName);
    if (!renamed.has(declaredName)) renamed.set(declaredName, nodeName);
    nodes.push({
      id: newNodeId(),
      nodeName,
      stepName: tn.stepName,
      stepVersion: tn.stepVersion ?? "0.1.0",
      needs: tn.needs ?? [],
      options: tn.options ?? {},
      position: stackedPosition(i)
    });
  });
  for (const node of nodes) {
    node.needs = node.needs.map((dep) => renamed.get(dep) ?? dep);
  }
  doc.nodes = nodes;
  return doc;
}
var DEFAULT_TEMPLATES = templatesFromWire(
  template_catalog_generated_default
);

// src/pipeline/revisions.ts
var wireNumber = (value) => num(value) ?? null;
function isoFromEpoch(value) {
  const seconds = wireNumber(value);
  return seconds === null ? null : new Date(seconds * 1e3).toISOString();
}
function revisionRefFromWire(raw) {
  if (!raw || typeof raw !== "object") return null;
  const w = raw;
  const revision = wireNumber(w.revision);
  const pipelineId = asString(w.pipeline_id);
  if (revision === null || !pipelineId) return null;
  return {
    pipelineId,
    revision,
    createdAt: isoFromEpoch(w.created_at),
    ...asString(w.created_by) ? { author: asString(w.created_by) } : {},
    ...asString(w.note) ? { note: asString(w.note) } : {},
    ...asString(w.label) ? { label: asString(w.label) } : {},
    contentHash: asString(w.content_hash) ?? ""
  };
}
function revisionsFromWire(payload) {
  if (!payload || typeof payload !== "object") return null;
  const w = payload;
  const current = wireNumber(w.current_version);
  if (current === null) return null;
  return {
    items: (Array.isArray(w.items) ? w.items : []).map(revisionRefFromWire).filter((r) => r !== null),
    live: wireNumber(w.live_version),
    current,
    nextCursor: asString(w.next_cursor) ?? null
  };
}
function revisionFromWire(payload) {
  const ref = revisionRefFromWire(payload);
  const doc = payload && typeof payload === "object" ? payload.doc : void 0;
  if (!ref || !doc || typeof doc !== "object") return null;
  return { ...ref, doc: docFromWire(doc) };
}
function revisionLabel(revision) {
  return typeof revision === "number" && revision >= 1 ? `r${revision}` : "";
}
function liveState(live, current) {
  if (live === null) {
    return {
      label: `Live: latest (${revisionLabel(current)})`,
      unpublished: 0,
      pinned: false,
      detail: "Runs execute the latest saved revision \u2014 every save goes live. Publish a revision to pin runs to it."
    };
  }
  const unpublished = Math.max(0, current - live);
  return {
    label: unpublished > 0 ? `Live ${revisionLabel(live)} \xB7 ${unpublished} unpublished` : `Live ${revisionLabel(live)}`,
    unpublished,
    pinned: true,
    detail: unpublished > 0 ? `Runs execute ${revisionLabel(live)}. The ${unpublished} later save${unpublished === 1 ? "" : "s"} (up to ${revisionLabel(current)}) run only once published.` : `Runs execute ${revisionLabel(live)}, the latest saved revision.`
  };
}
function publishCandidate(revisions, editing) {
  if (!revisions || revisions.live === null || editing !== revisions.current) return null;
  return liveState(revisions.live, revisions.current).unpublished > 0 ? revisions.current : null;
}

// src/pipeline/diff.ts
function canonical(value) {
  return JSON.stringify(
    value,
    (_key, v) => v && typeof v === "object" && !Array.isArray(v) ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => a.localeCompare(b))) : v
  );
}
function nodeFields(before, after) {
  const fields = [];
  if (before.stepName !== after.stepName || before.stepVersion !== after.stepVersion) fields.push("step");
  if (before.nodeName !== after.nodeName) fields.push("name");
  if (canonical(before.options) !== canonical(after.options)) fields.push("options");
  if (canonical([...before.needs].sort()) !== canonical([...after.needs].sort())) fields.push("dependencies");
  if (isNodeEnabled(before) !== isNodeEnabled(after)) fields.push("enabled");
  return fields;
}
function diffPipelineDocs(before, after) {
  const beforeNodes = new Map(before.nodes.map((n) => [n.id, n]));
  const afterNodes = new Map(after.nodes.map((n) => [n.id, n]));
  const changedNodes = [];
  for (const [id, node] of afterNodes) {
    const prior = beforeNodes.get(id);
    if (!prior) continue;
    const fields = nodeFields(prior, node);
    if (fields.length > 0) changedNodes.push({ nodeId: id, nodeName: node.nodeName, fields });
  }
  const settings = [];
  const settingValues = {};
  const scalar = (name, from, to) => {
    settings.push(name);
    settingValues[name] = { from, to };
  };
  if (before.name !== after.name) scalar("name", `\u201C${before.name}\u201D`, `\u201C${after.name}\u201D`);
  if ((before.description ?? "") !== (after.description ?? "")) settings.push("description");
  if (before.targetTier !== after.targetTier) {
    scalar("target tier", TIER_LABEL[before.targetTier] ?? before.targetTier, TIER_LABEL[after.targetTier] ?? after.targetTier);
  }
  if (before.dynamicStepBudget !== after.dynamicStepBudget) {
    scalar("dynamic budget", String(before.dynamicStepBudget), String(after.dynamicStepBudget));
  }
  if (before.parallelExecution !== after.parallelExecution) {
    scalar("parallel execution", before.parallelExecution ? "on" : "off", after.parallelExecution ? "on" : "off");
  }
  const beforeConfig = docConfigOf(before);
  const afterConfig = docConfigOf(after);
  const sections = /* @__PURE__ */ new Set([...Object.keys(beforeConfig), ...Object.keys(afterConfig)]);
  const configSections = [...sections].filter((key) => canonical(beforeConfig[key]) !== canonical(afterConfig[key])).sort();
  return {
    addedNodes: after.nodes.filter((n) => !beforeNodes.has(n.id)).map((n) => n.nodeName),
    removedNodes: before.nodes.filter((n) => !afterNodes.has(n.id)).map((n) => n.nodeName),
    changedNodes,
    settings,
    settingValues,
    configSections
  };
}
function isEmptyDiff(diff) {
  return diff.addedNodes.length === 0 && diff.removedNodes.length === 0 && diff.changedNodes.length === 0 && diff.settings.length === 0 && diff.configSections.length === 0;
}
function describeDiff(diff) {
  return [
    ...diff.addedNodes.map((name) => `Added step ${name}`),
    ...diff.removedNodes.map((name) => `Removed step ${name}`),
    ...diff.changedNodes.map((c) => `Changed ${c.nodeName}: ${c.fields.join(", ")}`),
    ...diff.settings.map((s) => {
      const v = diff.settingValues?.[s];
      return v ? `Changed ${s}: ${v.from} \u2192 ${v.to}` : `Changed ${s}`;
    }),
    ...diff.configSections.map((s) => `Changed config section ${s}`)
  ];
}

// src/pipeline/injection.ts
var InjectionAnchorKind = {
  END: "end",
  START: "start",
  AFTER: "after",
  BEFORE: "before"
};
var InjectionState = {
  APPLIED: "applied",
  SHADOWED: "shadowed",
  SKIPPED: "skipped",
  DISABLED: "disabled",
  NOT_TARGETED: "not_targeted",
  NOT_APPLICABLE: "not_applicable"
};
var InjectionSkipReason = {
  ANCHOR_MISSING: "anchor_missing",
  ANCHOR_AMBIGUOUS: "anchor_ambiguous",
  NAME_TAKEN: "name_taken",
  INVALID: "invalid"
};
function rungOf(value) {
  return canonicalRung(value) ?? "tenant";
}
function injectionScopeToWire(rung) {
  return rung === "global" ? "system" : rung;
}
function anchorFromWire(raw) {
  const w = raw && typeof raw === "object" ? raw : {};
  const kind = String(w.kind ?? "end");
  const nodeName = asString(w.node_name) ?? asString(w.nodeName);
  if ((kind === "after" || kind === "before") && nodeName) return { kind, nodeName };
  return kind === "start" ? { kind: "start" } : { kind: "end" };
}
function anchorToWire(anchor) {
  return anchor.kind === "after" || anchor.kind === "before" ? { kind: anchor.kind, node_name: anchor.nodeName } : { kind: anchor.kind };
}
function injectionFromWire(raw) {
  if (!raw || typeof raw !== "object") return null;
  const w = raw;
  const id = asString(w.id);
  const stepName = asString(w.step_name);
  if (!id || !stepName) return null;
  return {
    id,
    name: asString(w.name) ?? "",
    ...asString(w.description) ? { description: asString(w.description) } : {},
    scope: rungOf(w.scope),
    enabled: w.enabled !== false,
    priority: typeof w.priority === "number" ? w.priority : 100,
    stepName,
    stepVersion: asString(w.step_version) ?? "",
    nodeName: asString(w.node_name) ?? stepName,
    options: w.options && typeof w.options === "object" ? w.options : {},
    anchor: anchorFromWire(w.anchor),
    pipelineIds: Array.isArray(w.pipeline_ids) ? w.pipeline_ids.filter((p) => typeof p === "string") : []
  };
}
function injectionsFromResponse(payload) {
  const items = Array.isArray(payload) ? payload : payload && typeof payload === "object" && Array.isArray(payload.items) ? payload.items : [];
  return items.map(injectionFromWire).filter((r) => r !== null);
}
function injectionDraftToWire(draft) {
  const wire = {
    scope: injectionScopeToWire(draft.scope),
    step_name: draft.stepName,
    anchor: anchorToWire(draft.anchor),
    pipeline_ids: [...draft.pipelineIds]
  };
  if (draft.stepVersion) wire.step_version = draft.stepVersion;
  if (draft.nodeName && draft.nodeName !== draft.stepName) wire.node_name = draft.nodeName;
  if (draft.options && Object.keys(draft.options).length > 0) wire.options = draft.options;
  if (draft.enabled === false) wire.enabled = false;
  if (draft.priority !== void 0) wire.priority = draft.priority;
  if (draft.name) wire.name = draft.name;
  return wire;
}
function outcomeFromWire2(raw) {
  if (!raw || typeof raw !== "object") return null;
  const w = raw;
  const injectionId = asString(w.injection_id);
  if (!injectionId) return null;
  return {
    injectionId,
    nodeName: asString(w.node_name) ?? "",
    scope: rungOf(w.scope),
    state: asString(w.state) ?? InjectionState.APPLIED,
    step: asString(w.step) ?? "",
    reason: asString(w.reason) ?? null,
    shadowedBy: asString(w.shadowed_by) ?? null,
    nodeId: asString(w.node_id) ?? null,
    retuned: w.retuned === true,
    problems: Array.isArray(w.problems) ? w.problems.map(String) : [],
    flagsOff: Array.isArray(w.flags_off) ? w.flags_off.filter((f) => Boolean(f) && typeof f === "object").map((f) => ({ flag: String(f.flag ?? ""), setBy: String(f.set_by ?? "") })).filter((f) => f.flag !== "") : []
  };
}
function effectiveFromWire(payload) {
  if (!payload || typeof payload !== "object") return null;
  const w = payload;
  if (!w.doc || typeof w.doc !== "object") return null;
  const injections = Array.isArray(w.injections) ? w.injections.map(outcomeFromWire2).filter((o) => o !== null) : [];
  return { doc: docFromWire(w.doc), injections };
}
var INJECTION_RUNG_LABEL = {
  global: "Platform",
  customer: "Whole customer",
  org: "Organization",
  tenant: "Tenant",
  user: "Only my runs"
};
var INJECTION_RUNGS = RUNG_ORDER;
function describeAnchor(anchor) {
  switch (anchor.kind) {
    case "start":
      return "at the start";
    case "after":
      return `after ${anchor.nodeName}`;
    case "before":
      return `before ${anchor.nodeName}`;
    default:
      return "at the end";
  }
}
function describeOutcome(outcome, rows) {
  switch (outcome.state) {
    case InjectionState.APPLIED:
      if (outcome.flagsOff.length > 0) {
        const flags = outcome.flagsOff.map((f) => f.flag).join(", ");
        return {
          label: "Won't run",
          tone: "warning",
          detail: `In the pipeline, but skipped at run time: required feature flag ${flags} is off (${outcome.flagsOff[0].setBy}). Turn it on in this pipeline's config (feature_flags).`
        };
      }
      return outcome.retuned ? { label: "Retunes", tone: "applied", detail: `Lays its options over this pipeline's ${outcome.nodeName} node.` } : { label: "Applied", tone: "applied", detail: "Runs in every run of this pipeline at its rung." };
    case InjectionState.SHADOWED: {
      const winner = outcome.shadowedBy ? rows.get(outcome.shadowedBy) : void 0;
      return {
        label: "Overridden",
        tone: "inactive",
        detail: winner ? `The ${INJECTION_RUNG_LABEL[winner.scope]} injection of ${winner.nodeName} wins \u2014 this one is not applied.` : `Another injection of ${outcome.nodeName} wins \u2014 this one is not applied.`
      };
    }
    case InjectionState.SKIPPED:
      return { label: "Not applied", tone: "warning", detail: skipDetail(outcome) };
    case InjectionState.DISABLED:
      return { label: "Off", tone: "inactive", detail: "Switched off; it applies to nothing." };
    case InjectionState.NOT_TARGETED:
      return { label: "Other pipelines", tone: "inactive", detail: "Restricted to other pipelines." };
    case InjectionState.NOT_APPLICABLE:
      return {
        label: "Private runs only",
        tone: "inactive",
        detail: "A private injection only changes runs whose results are yours alone; this pipeline's runs are shared."
      };
    default:
      return { label: String(outcome.state), tone: "inactive", detail: String(outcome.state) };
  }
}
function skipDetail(outcome) {
  const why = outcome.problems[0];
  switch (outcome.reason) {
    case InjectionSkipReason.ANCHOR_MISSING:
      return `${why ?? "Its anchor node is not in this pipeline"} \u2014 it is skipped, not moved.`;
    case InjectionSkipReason.ANCHOR_AMBIGUOUS:
      return `${why ?? "Several nodes share its anchor's name"} \u2014 rename one so it can be placed.`;
    case InjectionSkipReason.NAME_TAKEN:
      return why ?? "The pipeline already has a node with this name running another step.";
    case InjectionSkipReason.INVALID:
      return `It would make the pipeline invalid${why ? `: ${why}` : ""}.`;
    default:
      return why ?? "The backend could not apply it.";
  }
}

// src/pipeline/simulation.ts
var SimulationEventKind = {
  /** Replay a historical transaction through the pipeline. */
  REPLAY_TX: "replay_tx",
  /** Inject a mock reorg over a block range. */
  REORG: "reorg",
  /** Promote a tx to a higher finality tier. */
  FINALITY: "finality"
};
function createReplayTxEvent(init) {
  return { id: newNodeId("ev"), kind: SimulationEventKind.REPLAY_TX, ...init };
}
function createReorgEvent(init) {
  return { id: newNodeId("ev"), kind: SimulationEventKind.REORG, ...init };
}
function createScenario(init = {}) {
  return {
    id: init.id ?? newNodeId("sim"),
    name: init.name ?? "New scenario",
    targetTier: init.targetTier ?? "confirmed",
    replayMode: init.replayMode ?? null,
    events: init.events ?? []
  };
}

// src/pipeline/integrations.ts
function isRunnableIntegration(row) {
  return row.enabled !== false && Boolean(row.live_version_id) && Boolean(row.live_function_version);
}
function unselectableReason(row, access) {
  if (row.enabled === false) return "Disabled";
  if (!row.live_version_id) return "No version published yet";
  if (!row.live_function_version) return "Promoted before versions were pinned \u2014 promote it again";
  if (access?.blind) return "No access granted \u2014 it would see nothing";
  return null;
}
function connectorsFromIntegrations(rows) {
  return rows.filter((row) => row.kind !== "internal" && isRunnableIntegration(row)).map((row) => ({
    id: row.runtime_id,
    name: row.name || row.runtime_id,
    description: row.description ?? void 0
  }));
}
function integrationsFromResponse(body) {
  if (Array.isArray(body)) return body;
  if (typeof body !== "object" || body === null) return [];
  const items = body.items;
  return Array.isArray(items) ? items : [];
}
var INTEGRATION_RUNTIME_IDS = ["python3.12", "python3.11"];
function integrationStarterFor(runtimeId) {
  if (runtimeId.startsWith("python")) return PY_INTEGRATION_STARTER;
  if (runtimeId.startsWith("node") || runtimeId.startsWith("typescript")) return NODE_INTEGRATION_STARTER;
  return null;
}
var PY_INTEGRATION_STARTER = `def handler(event, context):
    """Called by a pipeline's custom_step node.

    With batch_size 1 (the default) the event is ONE request and the function
    returns one result. With batch_size > 1 it is {"batch": [request, ...]} and
    the function returns {"results": [result, ...]}, in the same order and of
    the same length. Handling both keeps this working whichever is configured.
    """
    if "batch" in event:
        return {"results": [enrich(request) for request in event["batch"]]}
    return enrich(event)


def enrich(request):
    """One transaction in, its enrichments out.

    request["canonical"]    the transaction (chain_id, tx_hash, from/to, value, ...)
    request["enrichments"]  what earlier steps produced that this code may see
    request["options"]      this node's code_options
    request["tier"]         the latency tier it runs at

    Each enrichment is stored as custom.<kind> (kind: lowercase a-z, 0-9, _).
    "contract" is "free" (any object) or a typed one the platform validates:
      score           {"score": 0.0-1.0, "label": "..."}
      classification  {"label": "...", "scores": {"<label>": 0.0-1.0}}
      text            {"text": "...", "format": "plain" | "markdown"}
      risk_signal     {"score": 0.0-1.0, "category": "...", "reasons": ["..."]}
    """
    tx = request.get("canonical") or {}
    return {
        "status": "ok",
        "enrichments": [
            {
                "kind": "hello",
                "contract": "text",
                "payload": {"text": f"Saw {tx.get('tx_hash')} on {tx.get('chain_id')}"},
            }
        ],
    }
`;
var NODE_INTEGRATION_STARTER = `export async function handler(event) {
  // batch_size 1 (the default): \`event\` is ONE request; return one result.
  // batch_size > 1: \`event\` is { batch: [request, ...] }; return
  // { results: [result, ...] } in the same order and of the same length.
  if (Array.isArray(event.batch)) return { results: event.batch.map(enrich) };
  return enrich(event);
}

function enrich(request) {
  //   request.canonical    the transaction (chain_id, tx_hash, from/to, value, ...)
  //   request.enrichments  what earlier steps produced that this code may see
  //   request.options      this node's code_options
  // Each enrichment is stored as custom.<kind>; "contract" is "free" (any
  // object) or a typed one: score, classification, text, risk_signal.
  const tx = request.canonical ?? {};
  return {
    status: "ok",
    enrichments: [
      { kind: "hello", contract: "text", payload: { text: \`Saw \${tx.tx_hash} on \${tx.chain_id}\` } },
    ],
  };
}
`;

// src/pipeline/grants.ts
var ADDRESS_KIND = {
  kind: "address",
  label: "Address",
  keyLabel: "Address",
  hint: "Transactions where this address is the sender, the recipient or a participant.",
  placeholder: "0x\u2026 or a Solana / Bitcoin address",
  keyControl: "text",
  chainScoped: true
};
var GRANT_SUBJECT_KINDS = [
  {
    kind: "chain",
    label: "Whole chain",
    keyLabel: "Chain",
    hint: "Every transaction the pipeline processes on this chain. The broadest grant \u2014 use it while developing, then narrow.",
    placeholder: "",
    keyControl: "chain",
    chainScoped: false
  },
  ADDRESS_KIND,
  {
    kind: "label",
    label: "Label",
    keyLabel: "Label id",
    hint: "Transactions the pipeline labelled with this label id. Only matches if the labelling step runs BEFORE the integration.",
    placeholder: "label id",
    keyControl: "text",
    chainScoped: true
  },
  {
    kind: "tag",
    label: "Tag",
    keyLabel: "Tag id",
    hint: "Transactions carrying this tag id. Only matches if tagging runs BEFORE the integration.",
    placeholder: "tag id",
    keyControl: "text",
    chainScoped: true
  },
  {
    kind: "protocol",
    label: "Protocol",
    keyLabel: "Protocol slug",
    hint: "Transactions attributed to this protocol. Only matches if attribution runs BEFORE the integration.",
    placeholder: "protocol slug",
    keyControl: "text",
    chainScoped: true
  },
  {
    kind: "entity",
    label: "Entity",
    keyLabel: "Entity id",
    hint: "Transactions attributed to this entity id. Only matches if attribution runs BEFORE the integration.",
    placeholder: "entity id",
    keyControl: "text",
    chainScoped: true
  }
];
function grantSubjectKindInfo(kind) {
  return GRANT_SUBJECT_KINDS.find((row) => row.kind === kind) ?? ADDRESS_KIND;
}
function normalizeSubjectKey(kind, subjectKey, chainId) {
  const value = (subjectKey || "").trim();
  if (!value) return value;
  if (kind === "address") return normalizeAddress(value, chainId ?? void 0);
  if (kind === "chain") return toCanonicalChainId(value) ?? value;
  return value;
}
function validateGrantDraft(draft) {
  const chainId = draft.chain_id?.trim() || null;
  const key = normalizeSubjectKey(draft.subject_kind, draft.subject_key, chainId);
  if (!key) return "A subject is required \u2014 a grant that names nothing grants nothing.";
  if (draft.subject_kind === "chain" && !resolveChain(key)) {
    return "That is not a chain the platform knows, so no transaction would ever match it.";
  }
  if (draft.subject_kind === "address" && !isValidAddress(key, chainId)) {
    return "That does not look like an address. Check for a truncated paste.";
  }
  if (chainId && !resolveChain(chainId)) {
    return "The narrowing chain is not one the platform knows.";
  }
  if (draft.expires_at_ms != null && draft.expires_at_ms <= Date.now()) {
    return "An expiry in the past grants nothing.";
  }
  return null;
}
function isGrantActive(grant, nowMs = Date.now()) {
  if (grant.enabled === false) return false;
  if (grant.expires_at_ms != null && nowMs >= grant.expires_at_ms) return false;
  return true;
}
function describeGrant(grant) {
  const info = grantSubjectKindInfo(grant.subject_kind);
  const subject = grant.subject_kind === "chain" ? chainDisplayName(grant.subject_key) : grant.subject_key;
  const scope = grant.chain_id ? ` on ${chainDisplayName(grant.chain_id)}` : "";
  return `${info.label}: ${subject}${scope}`;
}
function emptyGrantDraft() {
  return { subject_kind: "chain", subject_key: DEFAULT_CHAIN_ID };
}
function inactiveGrantReason(grant, nowMs = Date.now()) {
  if (grant.enabled === false) return "Disabled";
  if (grant.expires_at_ms != null && nowMs >= grant.expires_at_ms) return "Expired";
  return null;
}
function accessSummary(grants, nowMs = Date.now()) {
  const total = grants.length;
  const active = grants.filter((grant) => isGrantActive(grant, nowMs)).length;
  const blind = active === 0;
  let sentence;
  if (total === 0) {
    sentence = "This integration has no access grants, so it will be skipped by every pipeline. Grant it a subject to make it run.";
  } else if (blind) {
    sentence = `All ${total} grant${total === 1 ? "" : "s"} ${total === 1 ? "is" : "are"} disabled or expired, so this integration will be skipped by every pipeline.`;
  } else if (grants.some((grant) => isGrantActive(grant, nowMs) && grant.subject_kind === "chain")) {
    sentence = `Sees every transaction on ${active === 1 ? "the granted chain" : "the granted subjects"}.`;
  } else {
    sentence = `Sees ${active} granted subject${active === 1 ? "" : "s"}.`;
  }
  return { total, active, blind, sentence };
}
function grantsFromResponse(body) {
  if (Array.isArray(body)) return body;
  if (typeof body !== "object" || body === null) return [];
  const items = body.items;
  return Array.isArray(items) ? items : [];
}

// src/pipeline/models.ts
function pipelineModelChoices(list) {
  const choices = /* @__PURE__ */ new Map();
  for (const rung of list.rungs) for (const model of rung.models) {
    const current = choices.get(model.name);
    if (!current || !current.champion && !current.shadowOnly && (model.champion || model.shadow_only)) choices.set(model.name, {
      name: model.name,
      rung: rung.scope,
      champion: model.champion,
      ...model.shadow_only ? { shadowOnly: true } : {}
    });
  }
  return [...choices.values()].sort((a, b) => a.name.localeCompare(b.name));
}
function isModelStep(step) {
  return step.category === "ml" || step.modelBinding != null;
}

export { BODY_SCOPES, DEFAULT_BODY_SCOPE, DEFAULT_RETRY_POLICY, DEFAULT_STEP_CATALOG, DEFAULT_TEMPLATES, FAILURE_POLICIES, FAILURE_POLICY_DESCRIPTION, FAILURE_POLICY_LABEL, FILTER_INDEXES_CONFIG_KEY, FailurePolicy, GRANT_SUBJECT_KINDS, INJECTION_RUNGS, INJECTION_RUNG_LABEL, INTEGRATION_RUNTIME_IDS, InjectionAnchorKind, InjectionSkipReason, InjectionState, KNOWN_DOC_CONFIG_SECTIONS, STEP_CATEGORY_LABEL, STEP_STATUS_LABEL, SimulationEventKind, StepCategory, StepDecisionReason, StepStatus, accessSummary, asBodyScope, catalogFromWire, connectorsFromIntegrations, createPipelineDoc, createReorgEvent, createReplayTxEvent, createScenario, defaultStepMetadata, describeAnchor, describeDiff, describeGrant, describeOutcome, diffPipelineDocs, docConfigOf, docFromWire, docToWire, downstreamOf, duplicatePipelineDoc, duplicatePipelineName, effectiveFromWire, emptyGrantDraft, filterIndexesOf, findNode, findStep, findTemplate, grantSubjectKindInfo, grantsFromResponse, groupByCategory, inactiveGrantReason, indexCatalog, injectionDraftToWire, injectionFromWire, injectionScopeToWire, injectionsFromResponse, instantiateTemplate, integrationStarterFor, integrationsFromResponse, isDeployablePipeline, isEmptyDiff, isEnrichmentStep, isGrantActive, isJsonOptionType, isModelStep, isNodeEnabled, isRunnableIntegration, isSkipReason, isSkipped, isTriggerStep, issueFromWire, issuesFromWire, liveState, newNodeId, nodeDisplayName, normalizeStepCategory, normalizeSubjectKey, outcomeFromWire2 as outcomeFromWire, outcomeReason, pipelineModelChoices, publishCandidate, renderPipelineMermaid, retriesEnabled, revisionFromWire, revisionLabel, revisionRefFromWire, revisionsFromWire, runResultFromWire, stepCategoryLabel, stepDisplayName, stepMetadataFromWire, stepRefId, summarizeOutcomes, templateFromWire, templateSamplesFor, templatesFromWire, topoSort, uniqueNodeName, unknownDocConfigSections, unselectableReason, validateGrantDraft, validatePipelineDoc, withDocConfig, withFilterIndexes, wouldCreateCycle };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map